/**
 * PlanHomeScreen
 *
 * Signal-driven, plan-based homepage.
 * Replaces the legacy onboarding-derived HomeScreen with a
 * stepper-based plan unique to each user's journey.
 * Supports both primary and partner views.
 */

import { getTodayString } from "@src/packages/utils/date";
import { appointmentKeys } from "@src/packages/care/patient-cache-keys";
import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  View,
  Text,
  ScrollView,
  Linking,
  Pressable,
  Modal,
} from "react-native";
import {
  CommonActions,
  useNavigation,
  type NavigationProp,
} from "@react-navigation/native";
import {
  UserCircle2,
  Activity,
  CalendarClock,
  PhoneCall,
  X,
  ArrowLeft,
  BookOpen,
} from "lucide-react-native";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import { useAppointments } from "@src/packages/care/hooks/useAppointments";
import { useUpdateAppointment } from "@src/packages/care/useCalendarData";
import {
  useAcknowledgePostProcedureOutcome,
  useActiveCycleWithPhases,
  useCreateCycleProcedureEvent,
  useCycleProcedureEvents,
  useCycles,
} from "@src/packages/cycles/hooks/useCycles";
import { hasConfirmedPregnancyProcedure } from "@src/packages/cycles/ivfFlowGuards";
import {
  DEV_ACTIVE_CYCLE_OVERRIDE,
  DEV_ACTIVE_CYCLE_SCENARIO,
} from "@src/packages/cycles/dev/devActiveCycleOverride";
import { getPostProcedureWaitContext } from "@src/packages/cycles/presenters/postProcedureWait";
import {
  getPostProcedureOutcomeContext,
  type PostProcedureOutcomeResultStatus,
} from "@src/packages/cycles/presenters/postProcedureOutcome";
import {
  isPostProcedureTransitionTimeReached,
  nextDayForPostProcedure,
  resolvePostProcedureTransitionCandidate,
  rescheduledProcedureEnd,
  serializePostProcedureTransitionRecord,
  shouldAutoPromptPostProcedureTransition,
  type PostProcedureTransitionCandidate,
  type PostProcedureEventKind,
  type PostProcedureTransitionStatus,
} from "@src/packages/cycles/presenters/postProcedureTransition";
import { DEV_FORCE_COMPLETE_CYCLE_OVERRIDE } from "@src/packages/cycles/dev/devCompleteCycleOverride";
import { SectionTitle, SectionTitleRow } from "@louise/moon-ui";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../App";
import type { Patient } from "@src/packages/patients/patients.types";
import type {
  CareStackParamList,
  AccessStackParamList,
  CostsStackParamList,
  ChatStackParamList,
  MainTabParamList,
} from "../types";
import { useDocumentUploadController } from "../DocumentUploadController";
import { useStartTreatmentController } from "../StartTreatmentController";
import { EMBER } from "../../onboarding/theme";
import { useLocale, useRegion, useTranslations } from "@src/packages/i18n";
import { formatDate } from "@src/packages/i18n/formatters";
import type { TranslationFunction } from "@src/packages/i18n";
import ScreenHeader from "../components/ScreenHeader";
import RoleAdaptedHomeBanner from "../components/RoleAdaptedHomeBanner";
import HelperJourneyHero from "../components/HelperJourneyHero";
import HelperHomeActionCard from "../components/HelperHomeActionCard";
import HelperJourneyStep from "../components/HelperJourneyStep";
import CompensationPlaceholderCard from "../components/CompensationPlaceholderCard";
import { resolveCycleDetailAction } from "@src/packages/homepage/plan/resolveCycleDetailAction";
import {
  recordedBirthHandoffCopyKeys,
  resolveRecordedBirth,
} from "@src/packages/homepage/plan/recordedBirth";
import { shouldShowEarlyPregnancyGuidance } from "@src/packages/homepage/plan/showEarlyPregnancyGuidance";
import { CarrierGuidedHome } from "@src/packages/homepage/plan/components/CarrierGuidedHome";
import type { CarrierJourneyScenario } from "@src/packages/homepage/plan/components/carrierJourney";
import { DEV_HELPER_ROLE_OVERRIDE } from "../components/devHelperRoleOverride";
import MedicalHistoryStepCard from "@src/packages/homepage/plan/components/MedicalHistoryStepCard";
import { PreCycleEggFreezingHome } from "@src/packages/homepage/plan/components/PreCycleEggFreezingHome";
import { PreCycleEmbryoFreezingHome } from "@src/packages/homepage/plan/components/PreCycleEmbryoFreezingHome";
import { PreCycleSpermFreezingHome } from "@src/packages/homepage/plan/components/PreCycleSpermFreezingHome";
import {
  GuidedPreCycleConceptHome,
  type GuidedPreCycleConceptVariant,
} from "@src/packages/homepage/plan/components/GuidedPreCycleConceptHome";
import {
  HomepageReviewConceptHome,
  TodayList,
  getPostProcedureOutcomeContentKey,
  getHomepageReviewConceptVariant,
  isPostProcedureOutcomeVariant,
  type CycleStatusReviewKind,
  type HomepageReviewConceptVariant,
  type HomepageMedicationFactSheetPayload,
  type HomepageProcedureFactSheetPayload,
  type HomepageTestFactSheetPayload,
  type TodayRow,
  type PeekAheadDay,
} from "@src/packages/homepage/plan/components/HomepageReviewConceptHome";
import { buildTodayRowsByDate } from "./care/suggested/conceptCalendarData";
import MedicationFactsheetSheet from "../components/MedicationFactsheetSheet";
import { AddMedicationModal } from "./care/modals/AddMedicationModal";
import type { EditMedicationData } from "./care/modals/AddMedicationModal";
import ProcedureDetailSheet from "../components/ProcedureDetailSheet";
import TestDetailSheet from "../components/TestDetailSheet";
import OvarianStimulationMonitoringSection from "./care/sections/OvarianStimulationMonitoringSection";
import RecordsSection, {
  RECORDS_CATEGORY_TO_DOC_CATEGORY,
  type RecordsCategory,
} from "./care/sections/RecordsSection";
import {
  useUserPersona,
  type PersonaHelperRole,
} from "@src/packages/persona/useUserPersona";
import {
  resolveHomeExperience,
  usesExploreHomeScenario,
  type HomeExperience,
} from "@src/packages/homepage/experience";
import { isPostRetrievalPhase } from "@src/packages/homepage/experience/isPostRetrievalPhase";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
  HEADER_INSET,
} from "@louise/moon-ui/theme/tokens";
import {
  usePatient,
  useUpdatePatient,
} from "@src/packages/patients/hooks/usePatient";
import {
  buildFertilityMetrics,
  useCreatePhase,
  useUpdatePhase,
} from "@src/packages/patients/hooks/usePhases";
import {
  useCreatePatientNote,
  usePatientNotes,
} from "@src/packages/care/hooks/usePatientNotes";
import { getHomepageState3D } from "@src/packages/homepage/homeStateUtils";
import {
  useHomepageCards,
  OptimizationCard,
  OptimizationCardV2,
  FETParticipantHomeView,
  PartnerHomeUnavailableView,
  ConditionInsightCard,
  CostEstimateCard,
  useConditionInsights,
  useTestResultInsights,
  resultInterpretationKey,
  useCostEstimate,
  getJourneyStepCompletionId,
} from "@src/packages/homepage/plan";
import { EGG_DONATION_GUIDE_ARTICLE_ID } from "@src/packages/homepage/plan/journeys";
import TestResultInsightCardWithDetail from "../components/TestResultInsightCardWithDetail";
import {
  useTreatmentDashboard,
  type UseTreatmentDashboardResult,
} from "@src/packages/homepage/plan/hooks/useTreatmentDashboard";
import { useCycleComplete } from "@src/packages/homepage/plan/hooks/useCycleComplete";
import { useFETParticipantViews } from "@src/packages/cycles/useFETParticipantViews";
import { useFETPlanForCycle } from "@src/packages/cycles/useFETPlan";
import {
  FETProcedureReportUnavailableError,
  useRecordFETProcedureReport,
  useReportFETTransferSelf,
} from "@src/packages/cycles/useFETProcedure";
import CycleCompleteView from "@src/packages/homepage/plan/components/CycleCompleteView";
import {
  resolveCycleClosingContent,
  resolveCycleClosingRetired,
} from "@src/packages/homepage/closing/cycleClosingSlides";
import {
  hasConcludedPregnancy,
  isCycleClosingArrival,
  isCycleEndingRecent,
  closedEndingInterruptsAnotherSubject,
  homeSubjectClosedCycle,
  NO_CYCLE_ENDING,
  readCycleEnding,
  shouldUseCycleEndingForHomeExperience,
} from "@src/packages/homepage/experience/selectHomeCycleEnding";
import { CycleClosingCarousel } from "@src/packages/homepage/closing/CycleClosingCarousel";
import { useCreateTreatmentSnapshot } from "@src/packages/cycles/hooks/usePastCycleDetail";
import { useCyclePregnancyFollowUp } from "@src/packages/homepage/pregnancy";
import { OutcomeFunnelModal } from "@src/packages/homepage/plan/components/OutcomeFunnelModal";
import {
  createIdempotencyKey,
  useFrozenSpecimens,
} from "@src/packages/care/cryovault/useSpecimens";
import {
  DEV_HOMEPAGE_ARCHIVE,
  DEV_PREVIEW_ALL_CARDS,
  MOCK_OPTIMIZATION_WINDOW,
  MOCK_CONDITION_CONTENT,
  getMockTestInsightsPreview,
} from "@src/packages/homepage/plan/dev/devCardPreview";
import { HomepageComponentArchive } from "@src/packages/homepage/plan/dev/HomepageComponentArchive";
import type {
  PlanAction,
  Pathway,
  OptimizationWindow,
  ResultInterpretation,
} from "@src/packages/homepage/plan";
import { resolvePlannedModality } from "@src/packages/homepage/plan/resolvePlannedModality";
import { resolveAcceptedPlanModality } from "@src/packages/homepage/plan/resolveAcceptedPlanModality";
import { useTreatmentPlan } from "@src/packages/homepage/plan/hooks/useTreatmentPlan";
import { useManualStepCompletion } from "@src/packages/homepage/plan/hooks/useManualStepCompletion";
import RoadmapModal, {
  resolveJourneyPath,
  resolveRoadmapPhaseId,
} from "./YouScreen/modals/RoadmapModal";
import { useJourneySignals } from "@src/packages/cycles/hooks/useJourneySignals";
import { selectJourneyCycle } from "@src/packages/cycles/presenters/journeyPhase";
import InsuranceInfoModal from "./YouScreen/modals/InsuranceInfoModal";
import IntentPickerModal, {
  type IntentPickerHomepageStage,
} from "./YouScreen/modals/IntentPickerModal";
import EndCycleModal from "./YouScreen/modals/EndCycleModal";
import PartnerInviteModal from "./YouScreen/modals/PartnerInviteModal";
import { useSendPartnerInvite } from "@src/packages/patients/hooks/usePartnerInvite";
import { usePartnerSharingScope } from "@src/packages/patients/hooks/usePartnerSharingScope";
import type { SharingCategory } from "@src/packages/patients/hooks/usePatientSharing";
import type { CycleType as StartTreatmentCycleType } from "./care/modals/MoonStartTreatmentModal";
import SupplementDetailSheet from "../components/SupplementDetailSheet";
import { getBlocksByKind, type ContentBlock } from "@src/packages/content";
import { useCoverage } from "@src/packages/coverage/hooks/useCoverage";
import { OrganicMiniIcon } from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import { JourneyHandoffCard } from "@src/packages/homepage/plan/components/JourneyHandoffCard";
import { PregnancyTrackingSetupDialog } from "@src/packages/homepage/plan/components/PregnancyTrackingSetupDialog";
import { OrganicExplanatoryIllustration } from "@src/packages/homepage/plan/components/EggFreezingBiologyDeepCarouselCard";
import { AiAssistantCard } from "../components/AiAssistantCard";
import {
  PostProcedureTransitionDialog,
  type PostProcedureTransitionResult,
} from "@src/packages/homepage/plan/components/PostProcedureTransitionDialog";
import ArticleSectionCard from "../components/ArticleSectionCard";
import ArticleBannerCard from "../components/ArticleBannerCard";
import { getWaitStageReadings } from "../content/waitStageReadings";
import { getStimStageReadings } from "../content/stimStageReadings";
import { useReadArticles } from "@louise/favorites";
import { useAuthGate } from "../AuthGateContext";
import { useSharedValue, withSpring } from "react-native-reanimated";
import {
  resolveActiveViewerRole,
  isCurrentPartnerParticipation,
  participantRoleToViewerRole,
} from "@src/packages/experience";
import { resolvePostCycleHandoffJourney } from "@src/packages/homepage/plan/postCycleHandoff";
import { PostCycleHandoffCard } from "@src/packages/homepage/plan/components/HomepageReviewConceptHome";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";
import {
  useCorrectFertilityGoal,
  type CorrectFertilityGoalPayload,
} from "@src/packages/fertility-intent";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
const clinicsIcon =
  require("../../assets/images/you-icons/clinics.png") as number;
const learnIcon = require("../../assets/images/you-icons/learn.png") as number;
const handoffBackground =
  require("../../assets/images/silk-brown.png") as number;

function buildPendingSemenAnalysisInsight(
  t: TranslationFunction,
): ResultInterpretation {
  return {
    testName: t("homepage.pending_semen_analysis.test_name"),
    testPurpose: t("homepage.pending_semen_analysis.test_purpose"),
    value: t("homepage.pending_semen_analysis.value"),
    unit: "",
    category: "normal",
    headline: t("homepage.pending_semen_analysis.headline"),
    interpretation: t("homepage.pending_semen_analysis.interpretation"),
    whatThisMeans: t("homepage.pending_semen_analysis.what_this_means"),
    nextStep: t("homepage.pending_semen_analysis.next_step"),
  };
}

function getDevSearch(): string | undefined {
  if (!__DEV__ || typeof window === "undefined") return undefined;
  const search = window.location.search;
  if (!search) return undefined;

  try {
    const hasDevParameter = [...new URLSearchParams(search).keys()].some(
      (key) => key.startsWith("dev_") || key.startsWith("force_"),
    );
    return hasDevParameter ? search : undefined;
  } catch {
    return undefined;
  }
}

function getDevSuggestedHomeSurface(): string | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("dev_home_suggested") ??
      params.get("dev_home_suggested_surface")
    );
  } catch {
    return null;
  }
}

function getDevOpenOverlay(): "outcome-funnel" | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const overlay = params.get("dev_open") ?? params.get("dev_open_overlay");
    return overlay === "outcome-funnel" ? overlay : null;
  } catch {
    return null;
  }
}

function getDevPostProcedurePrompt():
  "transfer" | "insemination" | "oi" | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const value = new URLSearchParams(window.location.search).get(
      "dev_post_procedure",
    );
    return value === "transfer" || value === "insemination" || value === "oi"
      ? value
      : null;
  } catch {
    return null;
  }
}

function stableProcedureEventRequestId(seed: string): string {
  let hash = 2166136261;
  for (const character of seed) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  const first = (hash >>> 0).toString(16).padStart(8, "0");
  const second = (Math.imul(hash, 31) >>> 0).toString(16).padStart(8, "0");
  const third = (Math.imul(hash, 131) >>> 0).toString(16).padStart(8, "0");
  const fourth = (Math.imul(hash, 257) >>> 0).toString(16).padStart(8, "0");
  const hex = `${first}${second}${third}${fourth}`.slice(0, 32);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-${(
    (Number.parseInt(hex.slice(16, 18), 16) & 0x3f) |
    0x80
  )
    .toString(16)
    .padStart(2, "0")}${hex.slice(18, 20)}-${hex.slice(20, 32)}`;
}

function localTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

function procedureEventDateTime(date: string, time: string | null): string {
  return `${date}T${time ?? "12:00"}:00`;
}

function getDevPatientMedicationActionsEnabled(): boolean {
  if (!__DEV__ || typeof window === "undefined") return false;
  try {
    return (
      new URLSearchParams(window.location.search).get(
        "dev_patient_medication_actions",
      ) === "true"
    );
  } catch {
    return false;
  }
}

function getDevPatientMedicationActionsMissingIdEnabled(): boolean {
  if (!getDevPatientMedicationActionsEnabled()) return false;
  try {
    return (
      new URLSearchParams(window.location.search).get(
        "dev_medication_missing_id",
      ) === "true"
    );
  } catch {
    return false;
  }
}

function getAutoHomepageReviewConceptVariant({
  homeExperience,
  treatmentData,
}: {
  homeExperience: HomeExperience;
  treatmentData: UseTreatmentDashboardResult;
}): HomepageReviewConceptVariant | null {
  const contextCycleType = homeExperience.context.treatment.cycleType;
  const cycleType =
    contextCycleType !== "unknown"
      ? contextCycleType
      : (treatmentData.rawCycleType ?? "unknown");
  const treatmentGoal = homeExperience.context.familyBuildingPlan.treatmentGoal;
  const primaryIntent = homeExperience.context.familyBuildingPlan.primaryIntent;
  const sexAtBirth = homeExperience.context.viewer.sexAtBirth;

  if (usesExploreHomeScenario(homeExperience.scenario)) {
    return "learn-explorer";
  }
  if (homeExperience.scenario === "trying_naturally") {
    return "trying-naturally";
  }
  if (homeExperience.scenario === "intended_parent_matching") {
    return "intended-parent";
  }
  // Carrier journeys have their own guided helper surface. Keep them out of
  // the donor concept variants, even when the carrier's primary intent is
  // derived as "donate" from the persona model.
  if (
    homeExperience.scenario === "carrier_application" ||
    homeExperience.scenario === "carrier_screening" ||
    homeExperience.scenario === "carrier_matched_legal"
  ) {
    return null;
  }
  // A donor in an active cycle reuses the preservation active home: an egg
  // donor's retrieval cycle mirrors egg freezing, a sperm donor's collection
  // mirrors sperm freezing. This must precede the generic donate check below,
  // which otherwise pins every donor to the static application view.
  if (homeExperience.scenario === "donor_active_cycle") {
    return sexAtBirth === "AMAB"
      ? "sperm-freezing-active"
      : "egg-freezing-active";
  }
  if (primaryIntent === "donate") {
    if (treatmentGoal === "sperm-donation" || sexAtBirth === "AMAB") {
      return "sperm-donor";
    }
    return "egg-donor";
  }
  if (homeExperience.scenario === "planning_iui") return "iui-precycle";
  if (homeExperience.scenario === "planning_ivf") return "ivf-precycle";
  if (homeExperience.scenario === "waiting_semen_results") {
    return "sperm-freezing-wait";
  }
  if (homeExperience.scenario === "waiting_beta") {
    const waitContext = getPostProcedureWaitContext(cycleType);
    if (waitContext.procedure === "ovulation_window") return "oi-wait";
    return waitContext.procedure === "insemination" ? "iui-wait" : "beta-wait";
  }
  if (homeExperience.scenario === "results_missing") {
    if (treatmentGoal === "sperm-freezing" || cycleType === "sperm_freezing") {
      return "sperm-freezing-wait";
    }
    if (
      cycleType === "ivf" ||
      cycleType === "icsi" ||
      cycleType === "fet" ||
      cycleType === "iui" ||
      cycleType === "oi"
    ) {
      return getPostProcedureOutcomeContext(cycleType, "unavailable").route;
    }
    const waitContext = getPostProcedureWaitContext(cycleType);
    if (waitContext.procedure === "ovulation_window") return "oi-wait";
    return waitContext.procedure === "insemination" ? "iui-wait" : "beta-wait";
  }
  if (
    homeExperience.scenario === "results_positive" ||
    homeExperience.scenario === "results_negative" ||
    homeExperience.scenario === "results_uncertain" ||
    homeExperience.scenario === "results_adverse"
  ) {
    const resultStatus: PostProcedureOutcomeResultStatus =
      homeExperience.scenario === "results_positive"
        ? "verified-positive"
        : homeExperience.scenario === "results_negative"
          ? "verified-negative"
          : homeExperience.scenario === "results_uncertain"
            ? "uncertain"
            : "adverse";
    return getPostProcedureOutcomeContext(cycleType, resultStatus).route;
  }
  if (
    homeExperience.scenario === "results_cancelled" ||
    homeExperience.scenario === "results_converted"
  ) {
    return "cycle-status-review";
  }
  if (homeExperience.scenario === "postcycle_storage") {
    if (
      treatmentGoal === "embryo-freezing" ||
      cycleType === "embryo_freezing"
    ) {
      return "embryo-freezing-postcycle";
    }
    if (treatmentGoal === "sperm-freezing" || cycleType === "sperm_freezing") {
      return "sperm-freezing-postcycle";
    }
    return "egg-freezing-postcycle";
  }

  if (homeExperience.surface === "active-treatment") {
    if (
      isPostRetrievalPhase({
        cycleType: cycleType === "unknown" ? undefined : cycleType,
        currentPhase: treatmentData.rawCurrentPhase,
        isInTreatment: treatmentData.isInTreatment,
      })
    ) {
      return "post-retrieval";
    }
    if (treatmentGoal === "egg-freezing" || cycleType === "egg_freezing") {
      return "egg-freezing-active";
    }
    if (
      treatmentGoal === "embryo-freezing" ||
      cycleType === "embryo_freezing"
    ) {
      return "embryo-freezing-active";
    }
    if (treatmentGoal === "sperm-freezing" || cycleType === "sperm_freezing") {
      return "sperm-freezing-active";
    }
    if (cycleType === "iui") return "iui-active";
    if (cycleType === "fet") return "fet-active";
    if (cycleType === "ivf" || cycleType === "icsi") return "ivf-active";
    if (cycleType === "oi") return "oi-active";

    /**
     * A cycle in a stimulation phase is a stimulated cycle, whatever type it
     * was recorded as — or whether it was recorded at all.
     *
     * Last of the active-treatment arms on purpose: every type with a home of
     * its own has already claimed it above, so this only catches what is left.
     * Before it, an ovulation-induction cycle and a cycle with no type reached
     * `return null` at the bottom of this function, and returning null does
     * not merely drop the hero card — the whole review-concept home is gated on
     * this variant, so the day's sections and the monitoring card went with it.
     * The screen kept its scaffolding, which is why it read as a homepage that
     * had lost its hero rather than as a homepage that had resolved to nothing.
     *
     * The scenario layer already names this state: `resolveExperienceScenario`
     * maps the protocol and stimulation phases to `active_ovarian_stimulation`.
     * The router simply never asked it, only the cycle's type.
     */
    if (homeExperience.scenario === "active_ovarian_stimulation") {
      return "oi-active";
    }
  }

  if (homeExperience.surface === "cycle-complete") {
    if (treatmentGoal === "egg-freezing" || cycleType === "egg_freezing") {
      return "egg-freezing-postcycle";
    }
    if (
      treatmentGoal === "embryo-freezing" ||
      cycleType === "embryo_freezing"
    ) {
      return "embryo-freezing-postcycle";
    }
    if (treatmentGoal === "sperm-freezing" || cycleType === "sperm_freezing") {
      return "sperm-freezing-postcycle";
    }
  }

  return null;
}

function getGuidedPreCycleConceptVariant(
  variant: HomepageReviewConceptVariant | null,
): GuidedPreCycleConceptVariant | null {
  switch (variant) {
    case "setup":
    case "learn-explorer":
    case "trying-naturally":
    case "ivf-precycle":
    case "iui-precycle":
    case "amab-family-building-precycle":
      return variant;
    default:
      return null;
  }
}

function shouldShowPendingSemenAnalysisCard({
  homeExperience,
  treatmentData,
  testInsights,
}: {
  homeExperience: HomeExperience;
  treatmentData: UseTreatmentDashboardResult;
  testInsights: ResultInterpretation[];
}): boolean {
  const treatmentGoal = homeExperience.context.familyBuildingPlan.treatmentGoal;
  const cycleType =
    homeExperience.context.treatment.cycleType !== "unknown"
      ? homeExperience.context.treatment.cycleType
      : (treatmentData.rawCycleType ?? "unknown");
  const isSpermFreezing =
    treatmentGoal === "sperm-freezing" || cycleType === "sperm_freezing";
  const isPendingSemenScenario =
    homeExperience.scenario === "waiting_semen_results" ||
    (homeExperience.scenario === "results_missing" && isSpermFreezing);
  const hasSemenResult = testInsights.some((insight) =>
    insight.testName.toLowerCase().includes("semen"),
  );

  return (
    isSpermFreezing &&
    isPendingSemenScenario &&
    !homeExperience.context.metrics.hasSemenSummary &&
    !hasSemenResult
  );
}

type HelperUpcomingStep = {
  title: string;
  subtitle: string;
  // When present the step renders as a pressable card (icon + chevron)
  // and navigates on press. When absent the step is a read-only preview.
  action?: { imageSource: number; screen: string };
};

// Scaffold steps below the active Medical History step on the donor / carrier
// home. These are read-only previews of the journey to reduce overwhelm —
// real product work on agencies, screening flow, contract upload, and cycle
// telemetry is tracked under task 1.5 part 5 in EXECUTION_PLAN.md.
function getHelperUpcomingSteps(
  t: TranslationFunction,
): Record<NonNullable<PersonaHelperRole>, HelperUpcomingStep[]> {
  return {
    donor: [
      {
        title: t("home.helper_steps.donor.apply_title"),
        subtitle: t("home.helper_steps.donor.apply_subtitle"),
        action: { imageSource: clinicsIcon, screen: "FindCareScreen" },
      },
      {
        title: t("home.helper_steps.donor.screened_title"),
        subtitle: t("home.helper_steps.donor.screened_subtitle"),
      },
      {
        title: t("home.helper_steps.donor.match_title"),
        subtitle: t("home.helper_steps.donor.match_subtitle"),
      },
      {
        title: t("home.helper_steps.donor.sign_title"),
        subtitle: t("home.helper_steps.donor.sign_subtitle"),
      },
      {
        title: t("home.helper_steps.donor.begin_title"),
        subtitle: t("home.helper_steps.donor.begin_subtitle"),
      },
    ],
    carrier: [
      {
        title: t("home.helper_steps.carrier.apply_title"),
        subtitle: t("home.helper_steps.carrier.apply_subtitle"),
        action: { imageSource: clinicsIcon, screen: "FindCareScreen" },
      },
      {
        title: t("home.helper_steps.carrier.screened_title"),
        subtitle: t("home.helper_steps.carrier.screened_subtitle"),
      },
      {
        title: t("home.helper_steps.carrier.match_title"),
        subtitle: t("home.helper_steps.carrier.match_subtitle"),
      },
      {
        title: t("home.helper_steps.carrier.sign_title"),
        subtitle: t("home.helper_steps.carrier.sign_subtitle"),
      },
      {
        title: t("home.helper_steps.carrier.begin_title"),
        subtitle: t("home.helper_steps.carrier.begin_subtitle"),
      },
    ],
    "intended-parent": [],
  };
}

/**
 * Derive pathway from patient data.
 * Currently checks onboarding partnerChoice; falls back to "solo".
 */
function derivePathway(patient: Patient | undefined): Pathway {
  const partnerChoice = patient?.partnerChoice as string | null | undefined;
  if (partnerChoice === "with-partner") return "with-partner";
  if (partnerChoice === "using-donor") return "with-donor";
  if (partnerChoice === "with-surrogate") return "with-surrogate";
  return "solo";
}

function isPartnerRole(patient: Patient | undefined): boolean {
  return isCurrentPartnerParticipation(
    patient?.participantRole,
    patient?.participantStatus,
  );
}

/**
 * Calculate user age from date of birth
 */
function calculateAge(dob: string | undefined): number | undefined {
  if (!dob) return undefined;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
}

/**
 * Get time-appropriate greeting
 */
function getGreeting(t: TranslationFunction, firstName?: string): string {
  const hour = new Date().getHours();
  const name = firstName ?? "";
  if (hour < 12) {
    return name
      ? t("home.greeting_morning_named", { params: { name } })
      : t("home.greeting_morning");
  }
  if (hour < 17) {
    return name
      ? t("home.greeting_afternoon_named", { params: { name } })
      : t("home.greeting_afternoon");
  }
  return name
    ? t("home.greeting_evening_named", { params: { name } })
    : t("home.greeting_evening");
}

/**
 * Navigation routing for plan actions.
 * Maps action screen identifiers to their tab + nested screen.
 */
/**
 * Extract screen names that accept undefined (no required params).
 */
type ScreensWithNoRequiredParams<T> = {
  [K in keyof T]: undefined extends T[K] ? K : never;
}[keyof T];

type NestedRoute =
  | { tab: "Care"; screen: ScreensWithNoRequiredParams<CareStackParamList> }
  | { tab: "Access"; screen: ScreensWithNoRequiredParams<AccessStackParamList> }
  | { tab: "Costs"; screen: ScreensWithNoRequiredParams<CostsStackParamList> }
  | { tab: "Learn"; screen: ScreensWithNoRequiredParams<ChatStackParamList> };

const SCREEN_MAP: Record<string, NestedRoute> = {
  AtHomeFertilityTesting: { tab: "Access", screen: "Testing" },
  FindCareScreen: { tab: "Access", screen: "FindCare" },
  DonorRegistryScreen: { tab: "Access", screen: "DonorRegistry" },
  DocumentsScreen: { tab: "Care", screen: "Documents" },
  MedicalHistory: { tab: "Care", screen: "MedicalHistory" },
  MenstrualHealthFlow: { tab: "Care", screen: "MenstrualHealthFlow" },
  CoverageFlow: { tab: "Costs", screen: "Insurance" },
  SupplementsScreen: { tab: "Access", screen: "Supplements" },
  ProgramsScreen: { tab: "Access", screen: "Programs" },
  CostsOverviewScreen: { tab: "Costs", screen: "CostsOverview" },
  PharmacyScreen: { tab: "Access", screen: "Pharmacy" },
  CycleDetailScreen: { tab: "Care", screen: "CycleDetail" },
  CycleDetail: { tab: "Care", screen: "CycleDetail" },
  CryoVaultScreen: { tab: "Care", screen: "CryoVault" },
  AllPastCycles: { tab: "Care", screen: "AllPastCycles" },
  LearnScreen: { tab: "Learn", screen: "Learn" },
  CareOverview: { tab: "Care", screen: "CareOverview" },
};

type PartnerOrDonorModalStep = "source" | "donor-status";

function PlanHomeModalChoiceRow({
  icon,
  title,
  subtitle,
  onPress,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("PlanHomeModalChoiceRow");
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(title), "plan_home_action_row");
        onPress();
      }}
      style={({ pressed }) => ({
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: pressed
          ? "rgba(92, 52, 32, 0.18)"
          : "rgba(92, 52, 32, 0.1)",
        backgroundColor: pressed
          ? "rgba(255,255,255,0.78)"
          : "rgba(255,255,255,0.56)",
        padding: 14,
      })}
    >
      <View
        style={{
          width: 48,
          height: 48,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </View>
      <View style={{ flex: 1, gap: 3 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            lineHeight: 19,
            color: EMBER.colors.text,
            fontWeight: "800",
          }}
        >
          {title}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 18,
            color: EMBER.colors.textMid,
            fontWeight: "500",
          }}
        >
          {subtitle}
        </Text>
      </View>
    </Pressable>
  );
}

function PartnerOrDonorModal({
  visible,
  step,
  onClose,
  onBack,
  onChoosePartner,
  onChooseDonor,
  onDonorAlreadySelected,
  onNeedDonorHelp,
}: {
  visible: boolean;
  step: PartnerOrDonorModalStep;
  onClose: () => void;
  onBack: () => void;
  onChoosePartner: () => void;
  onChooseDonor: () => void;
  onDonorAlreadySelected: () => void;
  onNeedDonorHelp: () => void;
}) {
  const capturePress = usePressCapture("PartnerOrDonorModal");
  const t = useTranslations();
  const isDonorStep = step === "donor-status";
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable
        onPress={() => {
          capturePress(pressLabel("dismiss_sheet"), "dismiss_sheet");
          onClose();
        }}
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.45)",
          justifyContent: "center",
          alignItems: "center",
          paddingHorizontal: 18,
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: 420,
            borderRadius: 22,
            backgroundColor: EMBER.colors.surface,
            padding: 18,
            borderWidth: 1,
            borderColor: "rgba(255,255,255,0.52)",
            shadowColor: "rgba(67, 30, 6, 0.32)",
            shadowOffset: { width: 0, height: 18 },
            shadowOpacity: 0.2,
            shadowRadius: 34,
            elevation: 8,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            {isDonorStep ? (
              <Pressable
                onPress={() => {
                  capturePress(pressLabel(t("home.back")), "home.back");
                  onBack();
                }}
                hitSlop={12}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <ArrowLeft
                  size={15}
                  color={EMBER.colors.textFaint}
                  strokeWidth={1.8}
                />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    color: EMBER.colors.textFaint,
                    fontWeight: "700",
                  }}
                >
                  {t("home.back")}
                </Text>
              </Pressable>
            ) : (
              <View />
            )}
            <Pressable
              onPress={() => {
                capturePress(pressLabel("close_sheet"), "close_sheet");
                onClose();
              }}
              hitSlop={12}
            >
              <X size={20} color={EMBER.colors.textFaint} strokeWidth={2} />
            </Pressable>
          </View>

          <View style={{ gap: 6, marginBottom: 18 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 22,
                lineHeight: 27,
                color: EMBER.colors.text,
                fontWeight: "800",
                letterSpacing: -0.2,
              }}
            >
              {isDonorStep
                ? t("home.donor_modal.title_donor_status")
                : t("home.donor_modal.title_source")}
            </Text>
          </View>

          <View style={{ gap: 10 }}>
            {isDonorStep ? (
              <>
                <PlanHomeModalChoiceRow
                  icon={<OrganicMiniIcon kind="donor" size={42} />}
                  title={t("home.donor_modal.donor_selected_title")}
                  subtitle={t("home.donor_modal.donor_selected_subtitle")}
                  onPress={onDonorAlreadySelected}
                />
                <PlanHomeModalChoiceRow
                  icon={<OrganicMiniIcon kind="find-care" size={42} />}
                  title={t("home.donor_modal.donor_need_help_title")}
                  subtitle={t("home.donor_modal.donor_need_help_subtitle")}
                  onPress={onNeedDonorHelp}
                />
              </>
            ) : (
              <>
                <PlanHomeModalChoiceRow
                  icon={<OrganicMiniIcon kind="partner" size={42} />}
                  title={t("home.donor_modal.partner_title")}
                  subtitle={t("home.donor_modal.partner_subtitle")}
                  onPress={onChoosePartner}
                />
                <PlanHomeModalChoiceRow
                  icon={<OrganicMiniIcon kind="donor" size={42} />}
                  title={t("home.donor_modal.donor_title")}
                  subtitle={t("home.donor_modal.donor_subtitle")}
                  onPress={onChooseDonor}
                />
              </>
            )}
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function CoverageStatusChoiceModal({
  visible,
  onClose,
  onHealthInsurance,
  onEmployerBenefits,
  onUnsure,
}: {
  visible: boolean;
  onClose: () => void;
  onHealthInsurance: () => void;
  onEmployerBenefits: () => void;
  onUnsure: () => void;
}) {
  const capturePress = usePressCapture("CoverageStatusChoiceModal");
  const t = useTranslations();
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable
        onPress={() => {
          capturePress(pressLabel("dismiss_sheet"), "dismiss_sheet");
          onClose();
        }}
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.45)",
          justifyContent: "center",
          alignItems: "center",
          paddingHorizontal: 18,
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: 420,
            borderRadius: 22,
            backgroundColor: EMBER.colors.surface,
            padding: 18,
            borderWidth: 1,
            borderColor: "rgba(255,255,255,0.52)",
            shadowColor: "rgba(67, 30, 6, 0.32)",
            shadowOffset: { width: 0, height: 18 },
            shadowOpacity: 0.2,
            shadowRadius: 34,
            elevation: 8,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "flex-end",
              marginBottom: 10,
            }}
          >
            <Pressable
              onPress={() => {
                capturePress(pressLabel("close_sheet"), "close_sheet");
                onClose();
              }}
              hitSlop={12}
            >
              <X size={20} color={EMBER.colors.textFaint} strokeWidth={2} />
            </Pressable>
          </View>

          <View style={{ gap: 6, marginBottom: 18 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 22,
                lineHeight: 27,
                color: EMBER.colors.text,
                fontWeight: "800",
                letterSpacing: -0.2,
              }}
            >
              {t("home.coverage_modal.title")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                lineHeight: 20,
                color: EMBER.colors.textLight,
                fontWeight: "500",
              }}
            >
              {t("home.coverage_modal.body")}
            </Text>
          </View>

          <View style={{ gap: 10 }}>
            <PlanHomeModalChoiceRow
              icon={<OrganicMiniIcon kind="costs" size={42} />}
              title={t("home.coverage_modal.health_insurance_title")}
              subtitle={t("home.coverage_modal.health_insurance_subtitle")}
              onPress={onHealthInsurance}
            />
            <PlanHomeModalChoiceRow
              icon={<OrganicMiniIcon kind="concierge" size={42} />}
              title={t("home.coverage_modal.employer_benefit_title")}
              subtitle={t("home.coverage_modal.employer_benefit_subtitle")}
              onPress={onEmployerBenefits}
            />
            <PlanHomeModalChoiceRow
              icon={<OrganicMiniIcon kind="find-care" size={42} />}
              title={t("home.coverage_modal.unsure_title")}
              subtitle={t("home.coverage_modal.unsure_subtitle")}
              onPress={onUnsure}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

export default function PlanHomeScreen() {
  const capturePress = usePressCapture("PlanHomeScreen");
  const t = useTranslations();
  const { openDocumentUpload, pickAndOpenDocumentUpload } =
    useDocumentUploadController();
  const { openStartTreatment } = useStartTreatmentController();
  const { locale } = useLocale();
  const region = useRegion();
  const { data: patient, isLoading } = usePatient();
  // Read journey-defining state through the canonical projection rather than
  // deriving it here. A signed-out viewer has no patient to fetch for, so the
  // underlying query stays disabled and never settles; only the projection
  // distinguishes "no request will be made" from "a request is in flight".
  const {
    canonical: canonicalExperienceContext,
    status: canonicalExperienceContextStatus,
  } = useAuthorizedExperienceProjection();
  const devSearch = getDevSearch();
  const { data: acceptedPlan } = useTreatmentPlan();
  const { data: cycles } = useCycles();
  const isPartner = useMemo(() => isPartnerRole(patient), [patient]);
  const persona = useUserPersona();
  const participantRole = useMemo(
    () =>
      resolveActiveViewerRole(
        devSearch !== undefined
          ? persona.treatmentGoal
          : (canonicalExperienceContext?.intent.goal ?? persona.treatmentGoal),
        participantRoleToViewerRole(patient?.participantRole),
      ),
    [
      canonicalExperienceContext?.intent.goal,
      devSearch,
      patient?.participantRole,
      persona.treatmentGoal,
    ],
  );
  const canonicalHelperRole = useMemo(() => {
    const role = resolveActiveViewerRole(
      canonicalExperienceContext?.intent.goal,
      participantRoleToViewerRole(
        canonicalExperienceContext?.viewer.participantRole,
      ),
    );
    return role === "donor" || role === "carrier" || role === "intended-parent"
      ? role
      : null;
  }, [
    canonicalExperienceContext?.intent.goal,
    canonicalExperienceContext?.viewer.participantRole,
  ]);
  const helperRole =
    DEV_HELPER_ROLE_OVERRIDE ??
    (devSearch !== undefined
      ? persona.journeyRole === "gestational-carrier"
        ? "carrier"
        : persona.journeyRole
      : canonicalExperienceContextStatus === "ready"
        ? canonicalHelperRole
        : null);
  const devOpenOverlay = getDevOpenOverlay();
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const scrollViewRef = useRef<ScrollView>(null);
  const { showAuthGate } = useAuthGate();
  const queryClient = useQueryClient();
  const updatePatient = useUpdatePatient();
  const correctFertilityGoal = useCorrectFertilityGoal();
  const sendPartnerInvite = useSendPartnerInvite();
  const {
    scope: partnerSharingScope,
    isAvailable: isPartnerSharingScopeAvailable,
  } = usePartnerSharingScope();
  const transitionCycle = useActiveCycleWithPhases();
  const fetPlanForCycleQuery = useFETPlanForCycle({
    cycleId: transitionCycle.cycle?.id ?? null,
    enabled: transitionCycle.cycle?.cycleType === "fet",
  });
  const recordFETProcedureReport = useRecordFETProcedureReport();
  const reportFETTransferSelf = useReportFETTransferSelf();
  const { mutateAsync: createTransitionPhase } = useCreatePhase();
  const { mutateAsync: updateTransitionPhase } = useUpdatePhase();
  const { mutateAsync: createProcedureEvent } = useCreateCycleProcedureEvent();
  const { mutateAsync: updateAppointment } = useUpdateAppointment();
  const { data: patientNotes = [] } = usePatientNotes();
  const { mutateAsync: createPatientNote } = useCreatePatientNote();
  const { data: procedureEvents = [] } = useCycleProcedureEvents(
    transitionCycle.cycle?.id,
  );

  // Derive 3D state + pathway
  const state3D = useMemo(() => getHomepageState3D(patient), [patient]);
  const pathway = useMemo(() => derivePathway(patient), [patient]);
  const plannedModality = useMemo(
    () =>
      acceptedPlan !== undefined && acceptedPlan !== null
        ? resolveAcceptedPlanModality(acceptedPlan)
        : resolvePlannedModality(patient?.fertilityIntent?.plannedModalities),
    [acceptedPlan, patient?.fertilityIntent?.plannedModalities],
  );
  const userAge = useMemo(
    () => calculateAge(patient?.dateOfBirth ?? undefined),
    [patient],
  );
  const { totalEggs, totalEmbryos } = useFrozenSpecimens();

  // Treatment dashboard data
  const treatmentData = useTreatmentDashboard();
  const { data: fetParticipantViews = [] } = useFETParticipantViews({
    enabled: isPartner && treatmentData.rawCycleType === "fet",
  });
  const fetParticipantView = fetParticipantViews[0];

  // "When to call your clinic" CTA has two states:
  //   - clinicPhone known: button dials it (tel: link)
  //   - clinicPhone unknown: button routes to FindCare so the user can
  //     pick their clinic, which fills in the gap in their profile
  //     (we learn where they're getting care) and unlocks the dial path
  //     once Clinic.phone is wired (see EXECUTION_PLAN cross-cutting).
  // Real Clinic.phone lookup needs backend (cycle.clinicId or a clinic
  // phone field on the care-team endpoint), so clinicPhone is null for
  // now and the "add my clinic" branch always renders.
  const clinicPhone = useMemo<string | null>(() => null, []);
  const handleCallClinic = useCallback(() => {
    if (!clinicPhone) return;
    void Linking.openURL(`tel:${clinicPhone.replace(/\s/g, "")}`);
  }, [clinicPhone]);
  const handleAddClinic = useCallback(() => {
    navigation.dispatch(
      CommonActions.navigate({
        name: "Access",
        params: { screen: "FindCare" },
      }),
    );
  }, [navigation]);
  const handleLogSymptoms = useCallback(() => {
    navigation.dispatch(CommonActions.navigate({ name: "Care" }));
  }, [navigation]);

  // Build today's concept agenda rows from the same adapter used by the
  // redesigned Care calendar, so home, Care, and cycle detail stay aligned.
  const todayIsoForSchedule = useMemo(() => getTodayString(), []);
  // Peek-ahead covers the next 3 days so the Today empty/done states can show
  // the real upcoming agenda instead of fixtures. One appointments query spans
  // today..+3 and one row-builder pass keys rows by date for both surfaces.
  const PEEK_AHEAD_DAY_COUNT = 3;
  const peekEndIsoForSchedule = useMemo(() => {
    const end = new Date(`${todayIsoForSchedule}T00:00:00`);
    end.setDate(end.getDate() + PEEK_AHEAD_DAY_COUNT);
    return end.toISOString().split("T")[0]!;
  }, [todayIsoForSchedule]);
  const { data: allPatientMeds } = usePatientMedications();
  const { data: scheduleAppointments } = useAppointments({
    startDate: todayIsoForSchedule,
    endDate: peekEndIsoForSchedule,
    // Closed/post-cycle users can still have medication doses or follow-up
    // visits in the next few days. Home owns the shared near-term agenda, so
    // keep this query available outside an open cycle as well.
    enabled: true,
  });
  const devPostProcedurePrompt = getDevPostProcedurePrompt();
  const postProcedureCandidate =
    useMemo<PostProcedureTransitionCandidate | null>(
      () =>
        resolvePostProcedureTransitionCandidate({
          cycle: transitionCycle.cycle,
          phases: transitionCycle.phases,
          appointments: scheduleAppointments ?? [],
          today: todayIsoForSchedule,
          fixtureProcedure:
            DEV_ACTIVE_CYCLE_OVERRIDE && devPostProcedurePrompt
              ? devPostProcedurePrompt
              : null,
          procedureEvents,
        }),
      [
        devPostProcedurePrompt,
        scheduleAppointments,
        todayIsoForSchedule,
        transitionCycle.cycle,
        transitionCycle.phases,
        procedureEvents,
      ],
    );
  const postProcedureEndReached =
    !postProcedureCandidate ||
    isPostProcedureTransitionTimeReached({
      candidate: postProcedureCandidate,
      today: todayIsoForSchedule,
    });
  const [postProcedureDialogOpen, setPostProcedureDialogOpen] = useState(false);
  const [pregnancyTrackingDialogVisible, setPregnancyTrackingDialogVisible] =
    useState(false);
  const [postProcedureDismissedKey, setPostProcedureDismissedKey] = useState<
    string | null
  >(null);
  const [postProcedureSaving, setPostProcedureSaving] = useState(false);
  const [postProcedureError, setPostProcedureError] = useState<string | null>(
    null,
  );
  const fetProcedureReportRequestIdRef = useRef<string | null>(null);
  const [endCycleVisible, setEndCycleVisible] = useState(false);
  const [pendingCycleCancellation, setPendingCycleCancellation] = useState<{
    candidate: PostProcedureTransitionCandidate;
    notes: string;
  } | null>(null);

  const shouldAutoOpenPostProcedureDialog = useMemo(() => {
    if (showAuthGate) return false;
    if (!postProcedureCandidate) return false;
    if (!postProcedureEndReached) return false;
    if (postProcedureDismissedKey === postProcedureCandidate.key) return false;
    return shouldAutoPromptPostProcedureTransition({
      candidate: postProcedureCandidate,
      notes: patientNotes,
      today: todayIsoForSchedule,
    });
  }, [
    patientNotes,
    postProcedureCandidate,
    postProcedureDismissedKey,
    postProcedureEndReached,
    showAuthGate,
    todayIsoForSchedule,
  ]);

  useEffect(() => {
    if (!postProcedureCandidate) {
      setPostProcedureDialogOpen(false);
      return;
    }
    if (shouldAutoOpenPostProcedureDialog) {
      setPostProcedureDialogOpen(true);
    }
  }, [postProcedureCandidate, shouldAutoOpenPostProcedureDialog]);

  useEffect(() => {
    if (!pendingCycleCancellation) return;
    const timeoutId = setTimeout(() => setEndCycleVisible(true), 350);
    return () => clearTimeout(timeoutId);
  }, [pendingCycleCancellation]);

  const writePostProcedureTransitionRecord = useCallback(
    async (
      candidate: PostProcedureTransitionCandidate,
      status: PostProcedureTransitionStatus,
      procedureDate: string,
      notes: string,
      procedureTime: string | null = candidate.procedureTime,
    ) => {
      const procedureEventKind: PostProcedureEventKind | null =
        candidate.procedureEventKind ??
        (candidate.procedure === "insemination"
          ? "insemination"
          : candidate.procedure === "ovulation_window"
            ? "ovulation_window"
            : null);
      if (procedureEventKind) {
        const eventStatus =
          status === "pending" ? "pending_confirmation" : status;
        const isOccurred = eventStatus === "occurred";
        const scheduledDate =
          eventStatus === "rescheduled"
            ? procedureDate
            : candidate.procedureDate;
        const scheduledTime =
          eventStatus === "rescheduled"
            ? procedureTime
            : candidate.procedureTime;
        await createProcedureEvent({
          cycleId: candidate.cycleId,
          body: {
            kind: procedureEventKind,
            status: eventStatus,
            sourceKind: isOccurred ? "patient_confirmed" : "patient_reported",
            scheduledStartAt: procedureEventDateTime(
              scheduledDate,
              scheduledTime,
            ),
            ...(isOccurred
              ? {
                  occurredAt: procedureEventDateTime(
                    procedureDate,
                    procedureTime,
                  ),
                }
              : {}),
            ...(eventStatus === "rescheduled" && candidate.procedureEventId
              ? { supersedesEventId: candidate.procedureEventId }
              : {}),
            timeZone: localTimeZone(),
            notes: notes || null,
            clientRequestId: stableProcedureEventRequestId(
              `${candidate.key}:${eventStatus}:${procedureDate}:${procedureTime ?? ""}`,
            ),
          },
        });
        return;
      }

      await createPatientNote({
        cycleId: candidate.cycleId,
        date: procedureDate,
        text: serializePostProcedureTransitionRecord({
          key: candidate.key,
          cycleId: candidate.cycleId,
          procedure: candidate.procedure,
          status,
          procedureDate,
          notes,
          recordedAt: new Date().toISOString(),
        }),
      });
    },
    [createPatientNote, createProcedureEvent],
  );

  const recordFETPatientProcedureReport = useCallback(
    async ({
      candidate,
      status,
      procedureDate,
      scheduledAt,
      procedureTime,
      embryosTransferred,
      notes,
    }: {
      candidate: PostProcedureTransitionCandidate;
      status: "awaiting_report" | "rescheduled" | "cancelled";
      procedureDate: string | null;
      scheduledAt: string | null;
      procedureTime: string | null;
      embryosTransferred: number | null;
      notes: string;
    }) => {
      if (
        candidate.source === "fixture" ||
        candidate.procedure !== "embryo_transfer" ||
        transitionCycle.cycle?.cycleType !== "fet"
      ) {
        return;
      }

      // The plan is no longer read here. Which plan backs this cycle, and which
      // of its versions the cycle was snapshotted from, is the server's decision
      // inside the self-report transaction — resolving it client-side meant
      // guessing it from a possibly-stale cache and sending the plan's *current*
      // version rather than the snapshotted one.
      const clientRequestId =
        fetProcedureReportRequestIdRef.current ?? createIdempotencyKey();
      fetProcedureReportRequestIdRef.current = clientRequestId;

      // A dated report of a transfer that happened goes through the self-report
      // endpoint: it stands the plan chain up when no clinic filed one — a
      // patient cannot, acceptance needs a named embryo in cryo storage and a
      // consent reference she does not hold — and otherwise reuses the plan
      // already snapshotted into the cycle, under a lock and against the
      // snapshotted version rather than a guess made from a stale cache here.
      if (status === "awaiting_report" && procedureDate) {
        await reportFETTransferSelf.mutateAsync({
          cycleId: candidate.cycleId,
          actualDate: procedureDate,
          clientRequestId,
          ...(candidate.appointmentId
            ? { appointmentId: candidate.appointmentId }
            : {}),
          ...(scheduledAt ? { scheduledAt } : {}),
          ...(procedureTime
            ? { actualTime: procedureTime, timeZone: localTimeZone() }
            : {}),
          ...(embryosTransferred != null
            ? { actualTransferCount: embryosTransferred }
            : {}),
          ...(notes ? { notes } : {}),
        });
        return;
      }

      // A rescheduling or a cancellation says the transfer did *not* happen, so
      // there is nothing to stand a plan up for. These still require a plan the
      // clinic filed, exactly as before: refusing them outright made "it was
      // rescheduled" and "the cycle was cancelled" unanswerable on an FET cycle,
      // and left the dialog reappearing with a developer string in it.
      if (fetPlanForCycleQuery.isPending) {
        throw new Error("The accepted FET plan is still loading");
      }
      if (fetPlanForCycleQuery.error) {
        throw fetPlanForCycleQuery.error;
      }
      const plan = fetPlanForCycleQuery.data;
      const version = plan?.versions.find(
        (item) => item.version === plan.currentVersion,
      );
      if (!plan || !version) {
        throw new FETProcedureReportUnavailableError();
      }

      await recordFETProcedureReport.mutateAsync({
        planId: plan.id,
        planVersion: version.version,
        cycleId: candidate.cycleId,
        status,
        ...(candidate.appointmentId
          ? { appointmentId: candidate.appointmentId }
          : {}),
        ...(scheduledAt ? { scheduledAt } : {}),
        ...(embryosTransferred != null
          ? { actualTransferCount: embryosTransferred }
          : {}),
        ...(notes ? { notes } : {}),
        clientRequestId,
      });
    },
    [
      fetPlanForCycleQuery,
      recordFETProcedureReport,
      reportFETTransferSelf,
      transitionCycle.cycle?.cycleType,
    ],
  );

  const handleDismissPostProcedureDialog = useCallback(async () => {
    if (!postProcedureCandidate) return;
    try {
      setPostProcedureSaving(true);
      setPostProcedureError(null);
      await writePostProcedureTransitionRecord(
        postProcedureCandidate,
        "dismissed",
        postProcedureCandidate.procedureDate,
        "",
      );
      setPostProcedureDialogOpen(false);
      setPostProcedureDismissedKey(postProcedureCandidate.key);
    } catch {
      // Keep the dialog open. A dismissal without a server acknowledgement
      // would reappear unpredictably on another device and cannot be treated
      // as durable state.
      setPostProcedureError(
        t("homepage.review_concept.post_procedure.save_failed"),
      );
    } finally {
      setPostProcedureSaving(false);
    }
  }, [postProcedureCandidate, t, writePostProcedureTransitionRecord]);

  const handlePostProcedureTransition = useCallback(
    async (result: PostProcedureTransitionResult) => {
      const candidate = postProcedureCandidate;
      if (!candidate) return;

      setPostProcedureError(null);
      if (result.status !== "occurred") {
        if (
          result.status === "cancelled" &&
          result.cancellationDisposition === "cycle_cancelled"
        ) {
          try {
            setPostProcedureSaving(true);
            await recordFETPatientProcedureReport({
              candidate,
              status: "cancelled",
              procedureDate: null,
              scheduledAt: result.procedureDate,
              procedureTime: result.procedureTime,
              embryosTransferred: result.embryosTransferred,
              notes: result.notes,
            });
            fetProcedureReportRequestIdRef.current = null;
            setPostProcedureDialogOpen(false);
            setPendingCycleCancellation({ candidate, notes: result.notes });
          } catch (error) {
            setPostProcedureError(
              error instanceof Error
                ? error.message
                : t("homepage.review_concept.post_procedure.save_failed"),
            );
          } finally {
            setPostProcedureSaving(false);
          }
          return;
        }
        if (
          result.status === "rescheduled" &&
          result.cancellationDisposition === "rescheduled"
        ) {
          try {
            setPostProcedureSaving(true);
            await recordFETPatientProcedureReport({
              candidate,
              status: "rescheduled",
              procedureDate: null,
              scheduledAt: result.procedureDate,
              procedureTime: result.procedureTime,
              embryosTransferred: result.embryosTransferred,
              notes: result.notes,
            });
            const scheduledTime =
              result.procedureTime ?? candidate.procedureTime;
            const scheduledEnd = rescheduledProcedureEnd({
              candidate,
              procedureDate: result.procedureDate,
              procedureTime: scheduledTime,
            });

            if (
              candidate.procedureEventKind ||
              candidate.procedure === "insemination" ||
              candidate.procedure === "ovulation_window"
            ) {
              await writePostProcedureTransitionRecord(
                candidate,
                "rescheduled",
                result.procedureDate,
                result.notes,
                scheduledTime,
              );
            }

            if (candidate.source === "appointment" && candidate.appointmentId) {
              await updateAppointment({
                appointmentId: candidate.appointmentId,
                startDate: result.procedureDate,
                ...(scheduledTime ? { startTime: scheduledTime } : {}),
                endDate: scheduledEnd.procedureEndDate,
                ...(scheduledEnd.procedureEndTime
                  ? { endTime: scheduledEnd.procedureEndTime }
                  : {}),
              });
            } else if (candidate.source === "phase") {
              const existingProcedurePhase = transitionCycle.phases.find(
                (phase) => phase.type === candidate.procedurePhaseType,
              );
              if (existingProcedurePhase?.id) {
                await updateTransitionPhase({
                  cycleId: candidate.cycleId,
                  phaseId: existingProcedurePhase.id,
                  body: {
                    startDate: result.procedureDate,
                    startTime: scheduledTime,
                  },
                });
              }
            }

            fetProcedureReportRequestIdRef.current = null;
            if (
              !candidate.procedureEventKind &&
              candidate.procedure !== "insemination" &&
              candidate.procedure !== "ovulation_window"
            ) {
              await writePostProcedureTransitionRecord(
                candidate,
                "rescheduled",
                result.procedureDate,
                result.notes,
              );
            }
            setPostProcedureDialogOpen(false);
            setPostProcedureDismissedKey(candidate.key);
            await queryClient.refetchQueries({
              queryKey: ["cycle-phases"],
              type: "all",
            });
            await queryClient.refetchQueries({
              queryKey: appointmentKeys.all,
              type: "all",
            });
          } catch (error) {
            setPostProcedureError(
              error instanceof Error
                ? error.message
                : t("homepage.review_concept.post_procedure.save_failed"),
            );
          } finally {
            setPostProcedureSaving(false);
          }
          return;
        }
        try {
          setPostProcedureSaving(true);
          await recordFETPatientProcedureReport({
            candidate,
            status:
              result.status === "cancelled" ? "cancelled" : "awaiting_report",
            procedureDate: null,
            scheduledAt: result.procedureDate,
            procedureTime: result.procedureTime,
            embryosTransferred: result.embryosTransferred,
            notes: result.notes,
          });
          await writePostProcedureTransitionRecord(
            candidate,
            result.status,
            result.procedureDate,
            result.notes,
          );
          fetProcedureReportRequestIdRef.current = null;
          setPostProcedureDialogOpen(false);
          setPostProcedureDismissedKey(candidate.key);
        } catch {
          setPostProcedureError(
            t("homepage.review_concept.post_procedure.save_failed"),
          );
        } finally {
          setPostProcedureSaving(false);
        }
        return;
      }

      try {
        setPostProcedureSaving(true);
        const procedureDate = result.procedureDate;
        await recordFETPatientProcedureReport({
          candidate,
          status: "awaiting_report",
          procedureDate,
          scheduledAt: null,
          procedureTime: result.procedureTime,
          embryosTransferred: result.embryosTransferred,
          notes: result.notes,
        });
        const endDate = nextDayForPostProcedure(procedureDate);
        const existingProcedurePhase = transitionCycle.phases.find(
          (phase) => phase.type === candidate.procedurePhaseType,
        );

        // A patient's report is the usable source in this patient-owned
        // product. Keep the typed FET procedure as awaiting_report so its
        // patient-reported provenance stays explicit, but do not make the
        // patient wait for a clinic connection or create generic legacy
        // transfer/luteal phases from this answer.
        if (
          candidate.source !== "fixture" &&
          candidate.procedure === "embryo_transfer" &&
          transitionCycle.cycle?.cycleType === "fet"
        ) {
          await writePostProcedureTransitionRecord(
            candidate,
            "occurred",
            procedureDate,
            result.notes,
          );
          fetProcedureReportRequestIdRef.current = null;
          setPostProcedureDialogOpen(false);
          setPostProcedureDismissedKey(candidate.key);
          return;
        }
        const fertilityMetrics =
          candidate.procedure === "embryo_transfer" &&
          result.embryosTransferred != null
            ? buildFertilityMetrics(
                { embryosTransferred: result.embryosTransferred },
                existingProcedurePhase,
              )
            : undefined;
        const inseminationInfo =
          candidate.procedure === "insemination"
            ? {
                ...(existingProcedurePhase?.inseminationInfo?.id
                  ? { id: existingProcedurePhase.inseminationInfo.id }
                  : {}),
                notes: result.notes || null,
              }
            : undefined;

        const hasTypedProcedureEvent =
          candidate.procedureEventKind ||
          candidate.procedure === "insemination" ||
          candidate.procedure === "ovulation_window";
        if (hasTypedProcedureEvent) {
          await writePostProcedureTransitionRecord(
            candidate,
            "occurred",
            procedureDate,
            result.notes,
            result.procedureTime,
          );
        }

        if (candidate.procedurePhaseType === "luteal_phase") {
          if (existingProcedurePhase?.id) {
            await updateTransitionPhase({
              cycleId: candidate.cycleId,
              phaseId: existingProcedurePhase.id,
              body: { startDate: procedureDate, endDate: null },
            });
          } else {
            await createTransitionPhase({
              cycleId: candidate.cycleId,
              body: { type: "luteal_phase", startDate: procedureDate },
            });
          }
        } else {
          const body = {
            startDate: procedureDate,
            startTime: result.procedureTime,
            endDate,
            ...(fertilityMetrics ? { fertilityMetrics } : {}),
            ...(inseminationInfo ? { inseminationInfo } : {}),
          };
          if (existingProcedurePhase?.id) {
            await updateTransitionPhase({
              cycleId: candidate.cycleId,
              phaseId: existingProcedurePhase.id,
              body,
            });
          } else {
            await createTransitionPhase({
              cycleId: candidate.cycleId,
              body: { type: candidate.procedurePhaseType, ...body },
            });
          }

          const existingLutealPhase = transitionCycle.phases.find(
            (phase) => phase.type === "luteal_phase",
          );
          if (existingLutealPhase?.id) {
            await updateTransitionPhase({
              cycleId: candidate.cycleId,
              phaseId: existingLutealPhase.id,
              body: { startDate: procedureDate, endDate: null },
            });
          } else {
            await createTransitionPhase({
              cycleId: candidate.cycleId,
              body: { type: "luteal_phase", startDate: procedureDate },
            });
          }
        }

        if (!hasTypedProcedureEvent) {
          await writePostProcedureTransitionRecord(
            candidate,
            "occurred",
            procedureDate,
            result.notes,
          );
        }
        setPostProcedureDialogOpen(false);
        await queryClient.refetchQueries({
          queryKey: ["cycle-phases"],
          type: "all",
        });
      } catch (error) {
        setPostProcedureError(
          error instanceof Error
            ? error.message
            : t("homepage.review_concept.post_procedure.save_failed"),
        );
      } finally {
        setPostProcedureSaving(false);
      }
    },
    [
      createTransitionPhase,
      postProcedureCandidate,
      queryClient,
      t,
      transitionCycle.cycle?.cycleType,
      transitionCycle.phases,
      updateAppointment,
      updateTransitionPhase,
      recordFETPatientProcedureReport,
      fetProcedureReportRequestIdRef,
      writePostProcedureTransitionRecord,
    ],
  );
  const scheduleRowsByDate = useMemo(() => {
    return buildTodayRowsByDate({
      medications: allPatientMeds ?? [],
      appointments: scheduleAppointments ?? [],
      testResults: [],
      symptoms: [],
      startDate: todayIsoForSchedule,
      endDate: peekEndIsoForSchedule,
      medicationDoseFallback: t("home.take_as_prescribed"),
      t,
      locale,
    });
  }, [
    allPatientMeds,
    scheduleAppointments,
    todayIsoForSchedule,
    peekEndIsoForSchedule,
    t,
    locale,
  ]);
  const todayConceptRows = useMemo<TodayRow[]>(() => {
    const rows = [...(scheduleRowsByDate.get(todayIsoForSchedule) ?? [])];
    if (
      getDevPatientMedicationActionsEnabled() &&
      !rows.some((row) => row.kind === "medication")
    ) {
      rows.unshift({
        id: `dev-gonal-f-${todayIsoForSchedule}`,
        date: todayIsoForSchedule,
        time: "7:00 AM",
        title: "Gonal-F 225 IU",
        detail: "Stimulation dose",
        icon: "hormones",
        status: "med",
        kind: "medication",
        medicationName: "Gonal-F",
        medicationId: getDevPatientMedicationActionsMissingIdEnabled()
          ? undefined
          : "15640000-0000-4000-8000-000000000001",
        medicationDose: "225 IU",
        medicationFormat: "Subcutaneous pen",
        medicationForm: "pen",
        scheduledFor: `${todayIsoForSchedule}T07:00:00`,
        medicationAvailabilityStatus: "on-hand",
      });
    }
    if (
      treatmentData.isInTreatment &&
      (DEV_ACTIVE_CYCLE_SCENARIO === "waiting_beta" ||
        DEV_ACTIVE_CYCLE_SCENARIO === "waiting_beta_pending")
    ) {
      // Two-week-wait fixture: the day is a luteal-support med, not a stim
      // monitoring visit. Only inject when nothing real is already scheduled.
      if (DEV_ACTIVE_CYCLE_OVERRIDE && rows.length === 0) {
        rows.push({
          id: `dev-progesterone-${todayIsoForSchedule}`,
          date: todayIsoForSchedule,
          time: "8:00 PM",
          title: "Progesterone",
          detail: "Evening dose",
          icon: "hormones",
          status: "med",
          kind: "medication",
          medicationName: "Progesterone",
        });
      }
    } else if (
      treatmentData.isInTreatment &&
      DEV_ACTIVE_CYCLE_OVERRIDE &&
      DEV_ACTIVE_CYCLE_SCENARIO === "default" &&
      !rows.some(
        (row) => row.kind === "appointment" || row.kind === "monitoring",
      )
    ) {
      rows.push({
        id: `dev-monitoring-${todayIsoForSchedule}`,
        appointmentId: `dev-monitoring-${todayIsoForSchedule}`,
        date: todayIsoForSchedule,
        time: "8:30 AM",
        title: "Monitoring appointment",
        detail: "Ultrasound + bloodwork",
        icon: "panel",
        kind: "monitoring",
        completable: true,
        procedureName: "Transvaginal ultrasound",
        appointmentResultType: "monitoring",
      });
    }
    return rows;
  }, [treatmentData.isInTreatment, scheduleRowsByDate, todayIsoForSchedule]);
  const peekAheadDays = useMemo<PeekAheadDay[]>(() => {
    // Closed/post-cycle users can still have a medication dose or follow-up
    // appointment in the next few days. Keep this tied to the real schedule
    // query for every concept-home state; the empty result is meaningful and
    // must not be replaced by a stimulation fixture.
    const base = new Date(`${todayIsoForSchedule}T00:00:00`);
    return Array.from({ length: PEEK_AHEAD_DAY_COUNT }, (_, i) => {
      const day = new Date(base);
      day.setDate(day.getDate() + i + 1);
      const iso = day.toISOString().split("T")[0]!;
      return {
        label:
          i === 0
            ? t("homepage.schedule.tomorrow")
            : formatDate(day, locale, { weekday: "long" }),
        rows: scheduleRowsByDate.get(iso) ?? [],
        emptyTitle: t("homepage.schedule.nothing_scheduled"),
        emptyDetail: t("homepage.schedule.nothing_scheduled_detail"),
      };
    });
  }, [scheduleRowsByDate, todayIsoForSchedule, t, locale]);

  // Journey map modal (RoadmapModal). MVP scaffolding for the long-term
  // gamified 3D journey; opened from the map icon in the cycle hero.
  const [journeyMapVisible, setJourneyMapVisible] = useState(false);
  // Only while the map is open — see `useJourneySignals`.
  const journeySignals = useJourneySignals({
    cycle: selectJourneyCycle(cycles),
    cycles,
    hasGoal: !!(
      patient?.fertilityIntent?.goal ?? patient?.treatments?.[0]?.intent
    ),
    enabled: journeyMapVisible,
  });
  const [outcomeFunnelVisible, setOutcomeFunnelVisible] = useState(
    devOpenOverlay === "outcome-funnel",
  );
  const [insuranceModalVisible, setInsuranceModalVisible] = useState(false);
  const [coverageStatusModalVisible, setCoverageStatusModalVisible] =
    useState(false);
  const [intentPickerVisible, setIntentPickerVisible] = useState(false);
  const [partnerOrDonorVisible, setPartnerOrDonorVisible] = useState(false);
  const [partnerOrDonorStep, setPartnerOrDonorStep] =
    useState<PartnerOrDonorModalStep>("source");
  const [partnerInviteVisible, setPartnerInviteVisible] = useState(false);
  const [partnerInviteName, setPartnerInviteName] = useState("");
  const [partnerInvitePhone, setPartnerInvitePhone] = useState("");
  const [reviewMedicationSheet, setReviewMedicationSheet] =
    useState<HomepageMedicationFactSheetPayload | null>(null);
  const [editMed, setEditMed] = useState<EditMedicationData | null>(null);
  const [reviewProcedureSheet, setReviewProcedureSheet] =
    useState<HomepageProcedureFactSheetPayload | null>(null);
  const [reviewTestSheet, setReviewTestSheet] =
    useState<HomepageTestFactSheetPayload | null>(null);

  useEffect(() => {
    if (devOpenOverlay === "outcome-funnel") {
      setOutcomeFunnelVisible(true);
    }
  }, [devOpenOverlay]);

  const handleOpenReviewMedicationFactSheet = useCallback(
    (payload: HomepageMedicationFactSheetPayload) => {
      setReviewMedicationSheet(payload);
    },
    [],
  );
  const handleEditMedicationById = useCallback(
    (medicationId: string, fallbackName?: string) => {
      const pm = allPatientMeds?.find((p) => p.id === medicationId);
      if (!pm) return;
      const timeTaken = Array.isArray(pm.timeTaken)
        ? (pm.timeTaken as string[])
        : [];
      setEditMed({
        id: pm.id,
        name: pm.medication?.name ?? fallbackName ?? "",
        dose: pm.dose,
        doseUnit: pm.doseUnit,
        route: pm.route ?? null,
        form: pm.form ?? null,
        timeTaken,
        startDate: pm.startDate,
        endDate: pm.endDate ?? null,
        notes: pm.notes,
      });
    },
    [allPatientMeds],
  );
  const handleOpenReviewProcedureFactSheet = useCallback(
    (payload: HomepageProcedureFactSheetPayload) => {
      setReviewProcedureSheet(payload);
    },
    [],
  );
  const handleOpenReviewTestFactSheet = useCallback(
    (payload: HomepageTestFactSheetPayload) => {
      setReviewTestSheet(payload);
    },
    [],
  );
  const partnerInviteError =
    sendPartnerInvite.error instanceof Error
      ? sendPartnerInvite.error.message
      : sendPartnerInvite.error
        ? String(sendPartnerInvite.error)
        : null;
  const {
    primaryPolicy,
    hasInsurance,
    verificationStatus,
    fertilityCoverage,
    isLoading: coverageLoading,
  } = useCoverage();

  // Supplement detail sheet — opened from a SupplementRow tap inside
  // ConditionInsightCard (or any future card that surfaces supplements).
  const [selectedSupplement, setSelectedSupplement] =
    useState<ContentBlock<"supplement"> | null>(null);
  const handleOpenSupplement = useCallback((name: string) => {
    const match = getBlocksByKind("supplement").find(
      (b) => b.title.toLowerCase() === name.toLowerCase(),
    );
    if (match) setSelectedSupplement(match);
  }, []);

  // Manual step completions (AsyncStorage-backed)
  const { markComplete: markStepComplete } = useManualStepCompletion();

  // Cycle complete data — `completeData.isComplete` is true whenever the
  // user has ANY closed cycle in their history, which permanently flags
  // returning users as "done." Gate on the current journey stage too so
  // exploring/before-treatment users with a past cycle aren't mistreated.
  const completeData = useCycleComplete(
    state3D?.goal === "preserve" ? null : (state3D?.goal ?? null),
    state3D?.goal === "embryo-preserve" ? "embryo_freezing" : undefined,
  );
  const isJourneyComplete =
    DEV_FORCE_COMPLETE_CYCLE_OVERRIDE ||
    (completeData.isComplete && state3D?.stage === "complete");
  /**
   * The cycle this Home is reporting on, and how it ended.
   *
   * While a cycle is open there is no ending to name. Once it closes, the
   * outcome exists only on the cycle record: `useTreatmentDashboard` is backed
   * by `/active-cycle`, which returns nothing for a closed cycle, so the most
   * recently ended cycle has to be found in the collection instead.
   *
   * This deliberately does not depend on the resolved Home variant — the
   * variant is derived *from* this — so it cannot reuse
   * `projectionSourceCycleId` below.
   */
  const homeCycleEnding = useMemo(() => {
    if (treatmentData.isInTreatment) {
      return {
        cycleType: treatmentData.rawCycleType,
        cycleStatus: "open",
        outcome: null,
        cycleId: null,
        outcomeId: null,
        outcomeType: null,
        acknowledgedAt: null,
        endDate: null,
        pregnancyOutcome: null,
        miscarriageOutcome: null,
        recordStatus: null,
      };
    }

    const latestClosed = homeSubjectClosedCycle(cycles);
    const ending = readCycleEnding(latestClosed);

    return {
      // Prefer the ended cycle's own type: the dashboard's type describes the
      // active cycle, and there isn't one.
      cycleType: ending.endedCycleType ?? treatmentData.rawCycleType,
      cycleStatus: latestClosed ? "closed" : undefined,
      ...ending,
    };
  }, [cycles, treatmentData.isInTreatment, treatmentData.rawCycleType]);

  /**
   * The closing carousel is Home's arrival state for a cycle that has just
   * ended, and acknowledging it is what retires it.
   *
   * Gated on a real outcome row rather than on the cycle merely being closed: a
   * cycle closed with no ending recorded has nothing to say, and `acknowledgedAt`
   * is the server's own record that the patient has seen this. That makes the
   * dismissal durable without inventing local storage for it.
   */
  const closingContent = useMemo(() => {
    if (!homeCycleEnding.outcomeId) return null;

    // A pregnancy that has since reached a birth or a loss must not be announced
    // as a pregnancy. The carousel is resolved from `outcome.type`, which stays
    // `"pregnancy"` after the ending is recorded, so
    // without this a patient who has just logged a birth could be met with
    // confetti telling them they are pregnant.
    if (hasConcludedPregnancy(homeCycleEnding)) return null;

    const content = resolveCycleClosingContent({
      cycleType: homeCycleEnding.cycleType,
      pathway,
      gender: state3D?.gender ?? null,
      goal: state3D?.goal ?? null,
      // The viewer's own relationship to the cycle is authoritative. Inferring
      // "supporting" from gender alone mislabels a female partner as the person
      // who carried the treatment.
      viewerIsSupporting: isPartner,
      // `outcome.type` is already exactly `PostProcedureOutcomeResult`.
      outcomeResult: homeCycleEnding.outcomeType,
      outcomeResultStatus: homeCycleEnding.outcome?.resultStatus,
      outcomeVerificationStatus: homeCycleEnding.outcome?.verificationStatus,
      // A retracted row resolves to the unsettled ending rather than the one it
      // names. Home already has words for that, so unlike a concluded pregnancy
      // this needs no guard of its own.
      outcomeRecordStatus: homeCycleEnding.outcome?.recordStatus,
    });

    // A null outcome type means two different things. On a preservation or
    // donation cycle it *is* the ending — `useCompleteCycle` closes those with a
    // deliberately null type and the display layer infers completion from the
    // cycle type. On a conceiving cycle it means nothing was ever recorded,
    // which is not an ending to announce.
    if (!homeCycleEnding.outcomeType && content.ending === "unresolved") {
      return null;
    }

    return content;
  }, [homeCycleEnding, pathway, state3D?.gender, state3D?.goal, isPartner]);

  /**
   * Whether pregnancy follow-up has actually begun. Once it has, the ending is
   * no longer the newest thing in the patient's care and Home should not greet
   * them with it.
   */
  // Server-held, not device-held: how far along someone is must read the same
  // on every device and to everyone looking after her.
  const closingPregnancyFollowUp = useCyclePregnancyFollowUp(
    homeCycleEnding.outcomeType === "pregnancy"
      ? homeCycleEnding.cycleId
      : null,
  );
  const pregnancyFollowUpStatus = closingPregnancyFollowUp.data?.status ?? null;

  /**
   * Stable, deliberately.
   *
   * Passed inline, this changed identity on every render of this screen — and
   * acknowledging an outcome invalidates the cycle queries, so it re-rendered
   * immediately. Anything downstream that waits for a *layout* to settle by
   * depending on this callback was really firing on the next render instead,
   * which is the bug it was written to avoid.
   */
  const scrollHomeToY = useCallback((y: number) => {
    scrollViewRef.current?.scrollTo({ y, animated: true });
  }, []);

  const closingRetired = useMemo(() => {
    if (!closingContent) return true;
    return resolveCycleClosingRetired({
      ending: closingContent.ending,
      family: closingContent.family,
      // A new cycle is the patient deciding what comes next, in their own time.
      hasOpenCycle: treatmentData.isInTreatment,
      pregnancyFollowUpStarted: pregnancyFollowUpStatus === "opted_in",
    });
  }, [closingContent, treatmentData.isInTreatment, pregnancyFollowUpStatus]);

  /**
   * Whether this ending is recent enough to greet the patient with.
   *
   * The carousel is an arrival state, and an arrival has to be about something
   * that just happened. Two flows create cycles that are *born* closed and carry
   * an outcome — "Add past cycle" in `AllPastCyclesScreen` and the
   * fertility-treatments medical-history import — so without this, entering a
   * past IVF from years ago would greet the patient as though the result had just
   * come in, and a positive one would fire confetti.
   *
   * This is a recency bound, not the timer that was deliberately rejected for
   * retirement. Retirement asks "is this still the most relevant thing on your
   * Home", which only an event can answer. Arrival asks "did this just happen",
   * which is a question about time by definition. An ending older than the window
   * gets no arrival — and having never been acknowledged, no standing card
   * either, which is the right outcome for historical data.
   */
  const closingEndedRecently = useMemo(() => {
    return isCycleEndingRecent(homeCycleEnding.endDate);
  }, [homeCycleEnding]);

  const createTreatmentSnapshot = useCreateTreatmentSnapshot();

  const acknowledgeClosing = useAcknowledgePostProcedureOutcome();

  /**
   * Acknowledging is a durable act, not a local dismissal: it writes
   * `outcome.acknowledgedAt`, so the carousel stays retired across devices and
   * reinstalls, and #2184's rule that a result must not be hidden by a dismissal
   * still holds — the quieter standing message keeps it reachable.
   */
  const handleAcknowledgeClosing = useCallback(() => {
    const { cycleId, outcomeId } = homeCycleEnding;
    if (!cycleId || !outcomeId) return;
    if (acknowledgeClosing.isPending) return;
    acknowledgeClosing.mutate({ cycleId, outcomeId });
  }, [acknowledgeClosing, homeCycleEnding]);

  const endingInterruptsAnotherSubject = useMemo(
    () => closedEndingInterruptsAnotherSubject(cycles),
    [cycles],
  );

  const closedEndingDrivesExperience = useMemo(
    () =>
      !treatmentData.isInTreatment &&
      shouldUseCycleEndingForHomeExperience(homeCycleEnding),
    [homeCycleEnding, treatmentData.isInTreatment],
  );

  /**
   * The ending recorded on a cycle that is still open.
   *
   * Recording a result deliberately does not close the cycle, so an open cycle
   * can already carry the result Home has to report. `homeCycleEnding` stays
   * about the *closed* cycle — the arrival carousel, its recency window and the
   * acknowledged pause are all built on a cycle that ended, and an open cycle has
   * no end date to be recent against — so this is a separate reading whose only
   * job is to let the experience resolver reach the result.
   *
   * `/active-cycle` already returns the outcome on an open cycle, so this costs no
   * extra request: it reads the cycle the treatment dashboard is already showing.
   */
  const openCycleEnding = useMemo(
    () =>
      treatmentData.isInTreatment
        ? readCycleEnding(transitionCycle.cycle ?? null)
        : NO_CYCLE_ENDING,
    [transitionCycle.cycle, treatmentData.isInTreatment],
  );

  /**
   * Gated on a named result for the same reason the closed reading is: an outcome
   * row that names nothing records nothing, and a cycle open without a named
   * result is simply still in progress.
   */
  const openEndingDrivesExperience = Boolean(openCycleEnding.outcomeType);

  /**
   * The outcome record Home is actually reporting on — the closed cycle's when it
   * drives the experience, the open cycle's when the result arrived before the
   * cycle was closed, and nothing when neither names a result.
   *
   * Named because two things read it now: the experience resolver, which turns it
   * into a surface, and the closing card at the foot of the page, whose copy has
   * to know whether that result has since reached a birth.
   */
  const experienceOutcome = useMemo(
    () =>
      closedEndingDrivesExperience
        ? homeCycleEnding.outcome
        : openEndingDrivesExperience
          ? openCycleEnding.outcome
          : null,
    [
      closedEndingDrivesExperience,
      homeCycleEnding.outcome,
      openCycleEnding.outcome,
      openEndingDrivesExperience,
    ],
  );

  const homeExperience = useMemo(
    () =>
      resolveHomeExperience({
        patient,
        canonicalContext: canonicalExperienceContext ?? undefined,
        canonicalContextStatus: canonicalExperienceContextStatus,
        devSearch,
        persona: { ...persona, modality: plannedModality },
        state3D,
        pathway,
        helperRole,
        isJourneyComplete,
        treatment: {
          isInTreatment: treatmentData.isInTreatment,
          cycleType: closedEndingDrivesExperience
            ? homeCycleEnding.cycleType
            : treatmentData.rawCycleType,
          currentPhase: treatmentData.rawCurrentPhase,
          cycleStatus: closedEndingDrivesExperience
            ? homeCycleEnding.cycleStatus
            : treatmentData.isInTreatment
              ? "open"
              : undefined,
          outcome: experienceOutcome,
          hasMonitoringSummary: !!treatmentData.monitoringSnapshot,
          hasCryoInventory: !!completeData.cryoStorage?.itemCount,
        },
      }),
    [
      patient,
      canonicalExperienceContext,
      canonicalExperienceContextStatus,
      devSearch,
      persona,
      plannedModality,
      state3D,
      pathway,
      helperRole,
      isJourneyComplete,
      treatmentData.isInTreatment,
      treatmentData.rawCurrentPhase,
      treatmentData.rawCycleType,
      treatmentData.monitoringSnapshot,
      completeData.cryoStorage?.itemCount,
      homeCycleEnding,
      closedEndingDrivesExperience,
      experienceOutcome,
    ],
  );
  const showPreCycleEggFreezingConcept =
    homeExperience.surface === "precycle-egg-freezing";
  const showPreCycleEmbryoFreezingConcept =
    homeExperience.surface === "precycle-embryo-freezing";
  const devSuggestedHomeSurface = getDevSuggestedHomeSurface();
  const devReviewConceptVariant = getHomepageReviewConceptVariant(
    devSuggestedHomeSurface,
  );
  const autoReviewConceptVariant = getAutoHomepageReviewConceptVariant({
    homeExperience,
    treatmentData,
  });
  const reviewConceptVariant =
    homeExperience.surface === "partner"
      ? null
      : (devReviewConceptVariant ?? autoReviewConceptVariant);
  const guidedPreCycleConceptVariant =
    getGuidedPreCycleConceptVariant(reviewConceptVariant);
  const scheduledPreCycleStartDate = useMemo(() => {
    if (!cycles) return null;
    const today = getTodayString();
    return (
      [...cycles]
        .filter(
          (cycle) =>
            cycle.status === "open" &&
            !!cycle.startDate &&
            cycle.startDate >= today,
        )
        .sort((a, b) => (a.startDate ?? "").localeCompare(b.startDate ?? ""))[0]
        ?.startDate ?? null
    );
  }, [cycles]);
  const reviewDashboardConceptVariant = guidedPreCycleConceptVariant
    ? null
    : reviewConceptVariant;
  const postProcedureOutcomeContentKey = getPostProcedureOutcomeContentKey(
    reviewDashboardConceptVariant,
  );
  const postProcedureOutcomeHandoffVariant =
    reviewDashboardConceptVariant?.endsWith("-positive")
      ? "positive"
      : reviewDashboardConceptVariant?.endsWith("-negative")
        ? "negative"
        : reviewDashboardConceptVariant?.endsWith("-unavailable")
          ? "unavailable"
          : "shared";
  const postProcedureOutcomeHandoffCopyKey = (
    field:
      | "title"
      | "body"
      | "cta"
      | "check_1"
      | "check_2"
      | "chat_prompt"
      | "secondary_cta",
  ) =>
    `homepage.review_concept.post_procedure_outcome.shared.handoff_${
      postProcedureOutcomeHandoffVariant === "shared"
        ? ""
        : `${postProcedureOutcomeHandoffVariant}_`
    }${field}`;

  /**
   * Whether the result this page reports has since reached a birth.
   *
   * The route cannot say: a birth keeps `type: "pregnancy"`, so it arrives on the
   * same `-positive` Home a beta drawn this morning does. See
   * `resolveRecordedBirth`, which the hero reads through the same rule.
   *
   * **The active cycle comes first, because that is the cycle the hero is about.**
   * Read from `experienceOutcome` alone, this disagreed with the hero: the hero
   * resolves its birth from the active cycle, while the experience outcome can be
   * a *different* record — a closed cycle's ending selected from the full cycle
   * list, or nothing at all when neither ending drives the surface. The result was
   * a page announcing a birth in its hero and still offering to set up pregnancy
   * follow-up at its foot. Whatever cycle the hero names, the closing card has to
   * be about the same one.
   */
  const recordedBirthOnHome = resolveRecordedBirth(
    transitionCycle.cycle?.outcome ?? experienceOutcome,
  );

  /**
   * Whether there is still an ending to state, and someone here who may state it.
   *
   * Read through the same rule the guidance and the hero use, from the same record
   * `recordedBirthOnHome` reads, so the closing card cannot disagree with either.
   * It answers three things at once: the pregnancy has not already concluded — a
   * birth *or* a loss both leave nothing to specify — and the viewer is the person
   * who carried the cycle, since recording an ending is not a partner's to do.
   */
  const standingPregnancyOnHome = shouldShowEarlyPregnancyGuidance({
    outcome: transitionCycle.cycle?.outcome ?? experienceOutcome,
    viewerIsSupporting: isPartner,
  });

  /**
   * The closing card's copy, resolved once for whichever voice it is in.
   *
   * A birth takes its own set of keys rather than a fifth `handoff_*` variant: the
   * shared template's leaves are `title`, `body` and `cta`, and this copy belongs
   * beside the hero's own birth words, under `homepage.recorded_birth`.
   */
  const outcomeHandoffCopy = recordedBirthOnHome
    ? recordedBirthHandoffCopyKeys()
    : {
        titleKey: postProcedureOutcomeHandoffCopyKey("title"),
        bodyKey: postProcedureOutcomeHandoffCopyKey("body"),
        ctaKey: postProcedureOutcomeHandoffCopyKey("cta"),
        checklistKeys: [
          postProcedureOutcomeHandoffCopyKey("check_1"),
          postProcedureOutcomeHandoffCopyKey("check_2"),
        ] as const,
        chatPromptKey: postProcedureOutcomeHandoffCopyKey("chat_prompt"),
      };

  const isPostProcedureOutcomeHome = isPostProcedureOutcomeVariant(
    reviewDashboardConceptVariant,
  );

  const isCycleStatusReviewHome =
    reviewDashboardConceptVariant === "cycle-status-review";

  /**
   * Whether the hero already states this ending and carries its acknowledgement.
   *
   * The carousel's suppression was written as "not a post-result Home", which
   * reads as the same condition but is not: `cycle-status-review` — the cancelled
   * surface — is not one of the twenty modality routes, so a cancellation got
   * both, the carousel announcing the ending above a hero announcing it again in
   * different words. Naming what it actually asks is what keeps "one ending, one
   * voice" true as surfaces are added.
   *
   * Gated on the ending being a closed one, because that variant is reachable
   * with the cycle still open — and there the hero's own action is "Close this
   * cycle", not an acknowledgement.
   */
  const heroCarriesTheEnding =
    isPostProcedureOutcomeHome ||
    (isCycleStatusReviewHome && closedEndingDrivesExperience);
  const cycleStatusReviewKind: CycleStatusReviewKind | null =
    isCycleStatusReviewHome && homeExperience.scenario === "results_cancelled"
      ? "cancelled"
      : isCycleStatusReviewHome &&
          homeExperience.scenario === "results_converted"
        ? "converted"
        : null;
  const cycleStatusReviewCopyPrefix = cycleStatusReviewKind
    ? `homepage.review_concept.inline.cycle_status_review_${cycleStatusReviewKind}`
    : "homepage.review_concept.inline.cycle_status_review";
  /**
   * Which of the three preservation journeys this post-cycle Home is about, so
   * the closing handoff card speaks about her material rather than in general —
   * and, being null everywhere else, what says "this is not a post-cycle Home"
   * to the blocks that ask.
   */
  const postCycleHandoffJourney = resolvePostCycleHandoffJourney(
    reviewDashboardConceptVariant,
  );
  const isPostCycleHome = postCycleHandoffJourney !== null;

  // Two-week-wait home extras: an inline "how are you feeling" chat (logging the
  // experience via conversation instead of a symptom checklist) and a curated
  // reading list for the stage. Only mounted on the beta-wait / iui-wait /
  // oi-wait homes.
  const isTwwHome =
    reviewDashboardConceptVariant === "beta-wait" ||
    reviewDashboardConceptVariant === "iui-wait" ||
    reviewDashboardConceptVariant === "oi-wait";
  const { isRead } = useReadArticles();
  const waitCycleType =
    homeExperience.context.treatment.cycleType !== "unknown"
      ? homeExperience.context.treatment.cycleType
      : treatmentData.rawCycleType;
  const waitReadings = useMemo(
    () => getWaitStageReadings(t, waitCycleType),
    [t, waitCycleType],
  );
  /**
   * `oi-active` is deliberately absent from this list.
   *
   * The reading it would add is the curated active-stimulation one, and that
   * article speaks of IVF outcomes, egg freezing, OHSS and retrieval — none of
   * which describes an ovulation induction cycle. A reading list for these days
   * is content work, not a list to borrow. `iui-active` is absent for the same
   * reason.
   */
  const isActiveStimHome = Boolean(
    reviewDashboardConceptVariant &&
    (reviewDashboardConceptVariant === "ivf-active" ||
      reviewDashboardConceptVariant === "egg-freezing-active" ||
      reviewDashboardConceptVariant === "embryo-freezing-active"),
  );
  const stimReadings = useMemo(
    () => (isActiveStimHome ? getStimStageReadings(t) : []),
    [isActiveStimHome, t],
  );
  const [waitChatExpanded, setWaitChatExpanded] = useState(false);
  const waitChatProgress = useSharedValue(0);
  const toggleWaitChat = useCallback(() => {
    const expanding = !waitChatExpanded;
    setWaitChatExpanded(expanding);
    waitChatProgress.value = withSpring(expanding ? 1 : 0, {
      damping: 28,
      stiffness: 180,
      mass: 0.8,
    });
  }, [waitChatExpanded, waitChatProgress]);

  const projectionSourceCycleId = useMemo(() => {
    const cycleType =
      reviewDashboardConceptVariant === "egg-freezing-postcycle"
        ? "egg_freezing"
        : reviewDashboardConceptVariant === "embryo-freezing-postcycle"
          ? "embryo_freezing"
          : null;
    if (!cycleType) return null;

    const latestClosedCycle = [...(cycles ?? [])]
      .filter(
        (cycle) => cycle.status === "closed" && cycle.cycleType === cycleType,
      )
      .sort((a, b) => {
        const aDate = a.endDate ?? a.updatedAt ?? a.createdAt;
        const bDate = b.endDate ?? b.updatedAt ?? b.createdAt;
        return bDate.localeCompare(aDate);
      })[0];

    return latestClosedCycle?.id ?? null;
  }, [cycles, reviewDashboardConceptVariant]);
  const showPreCycleSpermFreezingConcept =
    homeExperience.surface === "precycle-sperm-freezing";
  const showSuggestedPreCycleHome =
    showPreCycleEggFreezingConcept ||
    showPreCycleEmbryoFreezingConcept ||
    showPreCycleSpermFreezingConcept ||
    !!reviewConceptVariant;
  const startTreatmentModalDefaults = useMemo(() => {
    const treatmentGoal =
      homeExperience.context.familyBuildingPlan.treatmentGoal;
    const sexAtBirth = homeExperience.context.viewer.sexAtBirth;
    const gender: "male" | "female" | null =
      sexAtBirth === "AMAB" ? "male" : sexAtBirth === "AFAB" ? "female" : null;
    const presetCycleType: StartTreatmentCycleType | null =
      homeExperience.context.familyBuildingPlan.modality === "iui"
        ? "iui"
        : homeExperience.context.familyBuildingPlan.modality === "ivf"
          ? "ivf"
          : treatmentGoal === "egg-freezing"
            ? "egg_freezing"
            : treatmentGoal === "embryo-freezing"
              ? "embryo_freezing"
              : treatmentGoal === "sperm-freezing"
                ? "sperm_freezing"
                : null;

    return {
      goal:
        homeExperience.context.familyBuildingPlan.primaryIntent === "preserve"
          ? ("preserve" as const)
          : ("pregnancy" as const),
      gender,
      presetCycleType,
    };
  }, [homeExperience.context]);

  // Get cards and journey from the hook
  const contextModality = homeExperience.context.familyBuildingPlan.modality;
  const homepageModality = useMemo(() => {
    if (state3D?.stage === "trying-naturally") return "natural" as const;
    if (contextModality === "ivf" || contextModality === "iui") {
      return contextModality;
    }
    if (acceptedPlan !== undefined && acceptedPlan !== null) {
      return resolveAcceptedPlanModality(acceptedPlan);
    }
    const planned = patient?.fertilityIntent?.plannedModalities?.[0];
    if (planned === "ivf" || planned === "iui") return planned;
    return treatmentData.rawCycleType === "iui"
      ? "iui"
      : treatmentData.rawCycleType === "ivf" ||
          treatmentData.rawCycleType === "icsi"
        ? "ivf"
        : null;
  }, [
    contextModality,
    acceptedPlan,
    patient?.fertilityIntent?.plannedModalities,
    state3D?.stage,
    treatmentData.rawCycleType,
  ]);
  const { journey, cards, progress, activeStepIndex, activeStep } =
    useHomepageCards(
      state3D?.gender ?? null,
      state3D?.goal ?? null,
      pathway,
      homepageModality,
      participantRole === "unknown" ? null : participantRole,
    );

  // Intelligence layer
  const { insights: conditionInsights } = useConditionInsights(journey.steps);
  const { interpretations: testInsights, queryState: testResultsQueryState } =
    useTestResultInsights(userAge);
  const showPendingSemenAnalysisCard = shouldShowPendingSemenAnalysisCard({
    homeExperience,
    treatmentData,
    testInsights,
  });
  const { estimate: costEstimate } = useCostEstimate(
    state3D?.goal ?? null,
    state3D?.gender ?? null,
    homepageModality,
  );

  // Condition insights for the active step
  const activeStepConditionContent = useMemo(() => {
    const activeStep = journey.steps[activeStepIndex];
    if (!activeStep) return [];
    const insight = conditionInsights.find((i) => i.stepId === activeStep.id);
    return insight?.content ?? [];
  }, [conditionInsights, journey.steps, activeStepIndex]);

  const greeting = getGreeting(t, patient?.firstName ?? undefined);
  const handleOpenFullPlan = useCallback(() => {
    setJourneyMapVisible(true);
  }, []);

  const handleOpenIntentPicker = useCallback(() => {
    setIntentPickerVisible(true);
  }, []);

  const handleSelectIntent = useCallback(
    async (
      goal: string,
      homepageStage: IntentPickerHomepageStage = "explore",
    ) => {
      try {
        const fertilityIntent = patient?.fertilityIntent;
        if (fertilityIntent) {
          if (fertilityIntent.goal !== goal) {
            await correctFertilityGoal.mutateAsync({
              payload: {
                expectedVersion: fertilityIntent.version,
                goal: goal as CorrectFertilityGoalPayload["goal"],
                correctionSource: "patient",
                reason: "user_requested",
              },
            });
          }
          await updatePatient.mutateAsync({ homepageStage });
        } else {
          await updatePatient.mutateAsync({
            fertilityIntent: {
              goal: goal as NonNullable<
                NonNullable<Patient["fertilityIntent"]>["goal"]
              >,
            },
            homepageStage,
          });
        }
        setIntentPickerVisible(false);
        return true;
      } catch {
        return false;
      }
    },
    [correctFertilityGoal, patient?.fertilityIntent, updatePatient],
  );

  const handleOpenPartnerOrDonorModal = useCallback(() => {
    setPartnerOrDonorStep("source");
    setPartnerOrDonorVisible(true);
  }, []);

  const handleClosePartnerOrDonorModal = useCallback(() => {
    setPartnerOrDonorVisible(false);
    setPartnerOrDonorStep("source");
  }, []);

  const handleChoosePartnerSource = useCallback(() => {
    updatePatient.mutate({ partnerChoice: "with-partner" });
    setPartnerOrDonorVisible(false);
    setPartnerOrDonorStep("source");
    setPartnerInviteVisible(true);
  }, [updatePatient]);

  const handleChooseDonorSource = useCallback(() => {
    setPartnerOrDonorStep("donor-status");
  }, []);

  const handleDonorAlreadySelected = useCallback(() => {
    updatePatient.mutate({ partnerChoice: "using-donor" });
    setPartnerOrDonorVisible(false);
    setPartnerOrDonorStep("source");
    navigation.dispatch(
      CommonActions.navigate({
        name: "Care",
        params: {
          screen: "Documents",
          params: { initialCategory: "legal_consent" },
        },
      }),
    );
  }, [navigation, updatePatient]);

  const handleNeedDonorHelp = useCallback(() => {
    updatePatient.mutate({ partnerChoice: "using-donor" });
    setPartnerOrDonorVisible(false);
    setPartnerOrDonorStep("source");
    navigation.dispatch(
      CommonActions.navigate({
        name: "Access",
        params: { screen: "DonorRegistry" },
      }),
    );
  }, [navigation, updatePatient]);

  const handleSendPartnerInvite = useCallback(
    (name: string, phone: string, sharingCategories: SharingCategory[]) => {
      if (!partnerSharingScope) return;
      sendPartnerInvite.mutate({
        phone,
        firstName: name.trim() || undefined,
        sharingCategories,
        sharingScope: partnerSharingScope,
      });
    },
    [partnerSharingScope, sendPartnerInvite],
  );

  const handleChooseHealthInsurance = useCallback(() => {
    setCoverageStatusModalVisible(false);
    setInsuranceModalVisible(true);
  }, []);

  const handleChooseEmployerBenefits = useCallback(() => {
    setCoverageStatusModalVisible(false);
    navigation.dispatch(
      CommonActions.navigate({
        name: "Costs",
        params: { screen: "EmployerBenefits" },
      }),
    );
  }, [navigation]);

  const handleChooseCoverageUnsure = useCallback(() => {
    setCoverageStatusModalVisible(false);
    navigation.dispatch(
      CommonActions.navigate({
        name: "Costs",
        params: { screen: "CostsOverview" },
      }),
    );
  }, [navigation]);

  // Handle action presses — navigate to nested tab screens
  const handleAction = useCallback(
    (action: PlanAction) => {
      // Side-effect signals (no navigation)
      if (action.signal === "complete-step") {
        const stepId = action.params?.stepId;
        if (stepId) {
          void markStepComplete(
            getJourneyStepCompletionId(journey.key, stepId),
          );
        }
        return;
      }

      if (action.signal === "open-insurance-modal") {
        setInsuranceModalVisible(true);
        return;
      }

      if (action.signal === "open-coverage-status-modal") {
        setCoverageStatusModalVisible(true);
        return;
      }

      if (action.signal === "open-start-treatment-modal") {
        openStartTreatment({
          ...startTreatmentModalDefaults,
          presetCycleType:
            action.params?.presetCycleType ??
            startTreatmentModalDefaults.presetCycleType,
          source: "PlanHomeScreen",
        });
        return;
      }

      if (action.signal === "open-ai-chat") {
        const initialPrompt = action.params?.initialPrompt;
        navigation.dispatch(
          CommonActions.navigate({
            name: "Chat",
            params: {
              screen: "Assistant",
              params: initialPrompt
                ? { initialPrompt, autoSendInitialPrompt: true }
                : undefined,
            },
          }),
        );
        return;
      }

      if (action.signal === "open-partner-or-donor-modal") {
        handleOpenPartnerOrDonorModal();
        return;
      }

      if (action.signal === "acknowledge-closed-cycle") {
        // The same server field the closing carousel writes, reached from the
        // hero instead because that carousel is suppressed on a post-result
        // Home — see the gate below `closingContent`. Without this the ending
        // announced there could not be dismissed at all.
        handleAcknowledgeClosing();
        return;
      }

      if (action.signal === "close-recorded-cycle") {
        // Same window as the Care page's end-cycle CTA, so a cycle is ended
        // through one flow wherever the patient starts it. The modal preselects
        // the ending already recorded, so this confirms it rather than asking
        // for it again.
        //
        // Nothing is pending from this entry point: it carries no expectation of
        // a cancellation, so any stale one must not ride along.
        setPendingCycleCancellation(null);
        setEndCycleVisible(true);
        return;
      }

      if (action.signal === "open-recorded-cycle") {
        // The concept builders are static and hold no cycle, so a "review this
        // cycle" row built there cannot carry a cycleId — and a CycleDetailScreen
        // action without one is read below as a request to create a cycle. The
        // screen does know which cycle the result belongs to, so it resolves the
        // destination here: history for a closed cycle, the detail screen for an
        // open one, and the cycle list rather than a creation flow if neither.
        const closedCycleId = homeCycleEnding.cycleId;
        const openCycleId = treatmentData.cycleId;
        navigation.dispatch(
          CommonActions.navigate(
            closedCycleId
              ? {
                  name: "Care",
                  params: {
                    screen: "PastCycleDetail",
                    params: { cycleId: closedCycleId },
                  },
                }
              : openCycleId
                ? {
                    name: "Care",
                    params: {
                      screen: "CycleDetail",
                      params: {
                        cycleId: openCycleId,
                        // A caller can name the hub tab the result lives on, so
                        // "review the recorded result" lands on the result rather
                        // than on whichever step the cycle is currently at.
                        ...(action.params?.focus === "outcome"
                          ? { focus: "outcome" as const }
                          : {}),
                      },
                    },
                  }
                : { name: "Care", params: { screen: "AllPastCycles" } },
          ),
        );
        return;
      }

      if (action.signal === "open-intent-picker") {
        setIntentPickerVisible(true);
        return;
      }

      if (action.signal === "open-document-upload") {
        openDocumentUpload({
          defaultCategory: action.params?.initialCategory ?? "medical_records",
          initialFiles: action.documentFiles,
        });
        return;
      }

      // Cycle detail is an existing-cycle surface, and the concept builders are
      // static: an action meaning "this cycle" arrives without an id, so reading
      // every id-less one as a creation CTA sent "add monitoring data" to the
      // start-a-cycle flow. `resolveCycleDetailAction` owns that decision, where
      // it can be tested — the regression it guards, a creation CTA quietly
      // losing its route to start-treatment, is invisible to every other check.
      const cycleDetailTarget = resolveCycleDetailAction(action, {
        isInTreatment: treatmentData.isInTreatment,
        cycleId: treatmentData.cycleId,
      });

      if (cycleDetailTarget.kind === "open-cycle") {
        navigation.dispatch(
          CommonActions.navigate({
            name: "Care",
            params: {
              screen: "CycleDetail",
              params: {
                cycleId: cycleDetailTarget.cycleId,
                ...(cycleDetailTarget.focus
                  ? { focus: cycleDetailTarget.focus }
                  : {}),
              },
            },
          }),
        );
        return;
      }

      if (cycleDetailTarget.kind === "start-treatment") {
        openStartTreatment({
          ...startTreatmentModalDefaults,
          presetCycleType:
            action.params?.presetCycleType ??
            startTreatmentModalDefaults.presetCycleType,
          source: "PlanHomeScreen",
        });
        return;
      }

      if (!action.screen) return;

      if (
        action.screen === "DocumentsScreen" &&
        action.params?.openUpload === "true"
      ) {
        openDocumentUpload({
          defaultCategory: action.params.initialCategory ?? null,
        });
        return;
      }

      // Article deep links: screen=ArticleDetail, params.articleId
      if (action.screen === "ArticleDetail" && action.params?.articleId) {
        navigation.dispatch(
          CommonActions.navigate({
            name: "Learn",
            params: {
              screen: "ArticleDetail",
              params: { articleId: action.params.articleId, returnTab: "Home" },
            },
          }),
        );
        return;
      }

      // Cycle history deep link: screen=PastCycleDetail, params.cycleId.
      // Handled here rather than in SCREEN_MAP because `NestedRoute` admits only
      // screens whose params are optional, and this one requires a cycleId —
      // there is no such thing as "the" past cycle.
      if (action.screen === "PastCycleDetail" && action.params?.cycleId) {
        navigation.dispatch(
          CommonActions.navigate({
            name: "Care",
            params: {
              screen: "PastCycleDetail",
              params: { cycleId: action.params.cycleId },
            },
          }),
        );
        return;
      }

      const route = SCREEN_MAP[action.screen];
      if (!route) return;
      // Forward any params the action carried (e.g. cycleId for CycleDetail)
      const shouldReturnToHome =
        route.tab === "Access" && route.screen === "FindCare";
      const innerParams = action.params
        ? {
            ...action.params,
            ...(shouldReturnToHome && !action.params.returnTab
              ? { returnTab: "Home" }
              : {}),
          }
        : shouldReturnToHome
          ? { returnTab: "Home" }
          : undefined;
      navigation.dispatch(
        CommonActions.navigate({
          name: route.tab,
          params: innerParams
            ? { screen: route.screen, params: innerParams }
            : { screen: route.screen },
        }),
      );
    },
    [
      navigation,
      journey.key,
      homeCycleEnding.cycleId,
      treatmentData.cycleId,
      treatmentData.isInTreatment,
      markStepComplete,
      handleAcknowledgeClosing,
      handleOpenPartnerOrDonorModal,
      openDocumentUpload,
      openStartTreatment,
      startTreatmentModalDefaults,
    ],
  );

  /**
   * Open the cycle the closing message is about. Shared by the carousel and the
   * standing card so both land on the same place, and defined here because it
   * needs `handleAction`.
   */
  const openEndedCycle = useCallback(() => {
    if (!homeCycleEnding.cycleId) return;
    handleAction({
      label: t("homepage.cycle_closing.ui.open_cycle"),
      screen: "PastCycleDetail",
      params: { cycleId: homeCycleEnding.cycleId },
    });
  }, [handleAction, homeCycleEnding.cycleId, t]);

  const partnerSourceOverlays = (
    <>
      <PartnerOrDonorModal
        visible={partnerOrDonorVisible}
        step={partnerOrDonorStep}
        onClose={handleClosePartnerOrDonorModal}
        onBack={() => setPartnerOrDonorStep("source")}
        onChoosePartner={handleChoosePartnerSource}
        onChooseDonor={handleChooseDonorSource}
        onDonorAlreadySelected={handleDonorAlreadySelected}
        onNeedDonorHelp={handleNeedDonorHelp}
      />
      <PartnerInviteModal
        visible={partnerInviteVisible}
        name={partnerInviteName}
        phone={partnerInvitePhone}
        onChangeName={setPartnerInviteName}
        onChangePhone={setPartnerInvitePhone}
        onClose={() => setPartnerInviteVisible(false)}
        onSend={handleSendPartnerInvite}
        isSending={sendPartnerInvite.isPending}
        sent={sendPartnerInvite.isSuccess}
        scopeReady={isPartnerSharingScopeAvailable}
        error={partnerInviteError}
        onDismissError={() => sendPartnerInvite.reset()}
      />
      <CoverageStatusChoiceModal
        visible={coverageStatusModalVisible}
        onClose={() => setCoverageStatusModalVisible(false)}
        onHealthInsurance={handleChooseHealthInsurance}
        onEmployerBenefits={handleChooseEmployerBenefits}
        onUnsure={handleChooseCoverageUnsure}
      />
    </>
  );

  if (isLoading || homeExperience.journeyResolutionStatus === "loading") {
    return (
      <GlassScreenLayout>
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              fontWeight: "400",
            }}
          >
            {t("home.loading")}
          </Text>
        </View>
      </GlassScreenLayout>
    );
  }

  // FR users on a donation/surrogacy path see a coming-soon placeholder —
  // the helper paths (donor screening, surrogacy agreements) are
  // US-specific in this iteration. We gate on the primary journey rather than
  // a relationship record because onboarding has not created a real
  // co-fertility relationship yet.
  if (
    homeExperience.context.familyBuildingPlan.primaryIntent === "donate" &&
    region !== "US"
  ) {
    return (
      <GlassScreenLayout>
        <ScrollView
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
            paddingBottom: 100,
            maxWidth: 520,
            width: "100%",
            alignSelf: "center",
          }}
          showsVerticalScrollIndicator={false}
        >
          <ScreenHeader title={greeting} />
          <View
            style={{
              ...(GLASS_CARD_STYLE as object),
              padding: 24,
              alignItems: "center",
              gap: 12,
              marginTop: 12,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 18,
                fontWeight: "600",
                color: EMBER.colors.text,
                textAlign: "center",
              }}
            >
              {t("home.helper_not_available_title")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                color: EMBER.colors.textLight,
                textAlign: "center",
                lineHeight: 21,
              }}
            >
              {t("home.helper_not_available_body")}
            </Text>
          </View>
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  // Donors and carriers don't have their own fertility journey on Louise —
  // they're providing gametes / carrying for someone else. Suppress the
  // cycle / plan / journey content and surface only the role-adapted banner
  // and compensation placeholder. Baseline cards (Medical History, Documents,
  // Learn, Find Care) stay reachable via the bottom tab bar.
  // Exception: a donor who has started their retrieval/collection cycle drops
  // out of this helper hero and into the active-treatment home (resolved above
  // to an egg/sperm-freezing-active variant), since that cycle is identical to
  // the preservation pathway. Carriers have no such cycle and stay here.
  // Older records may have primaryIntent "donate" but no journeyRole. Derive
  // a render role so donors get the helper home instead of a blank
  // screen — the "egg-donor"/"sperm-donor" review-concept variants have no
  // concept/heroCopy entry, so falling through renders null (empty homepage).
  const effectiveHelperRole: "donor" | "carrier" | null =
    helperRole === "donor" || helperRole === "carrier"
      ? helperRole
      : homeExperience.context.familyBuildingPlan.primaryIntent === "donate"
        ? "donor"
        : null;
  const donorInActiveCycle =
    effectiveHelperRole === "donor" && treatmentData.isInTreatment;
  if (
    (effectiveHelperRole === "donor" ||
      (effectiveHelperRole === "carrier" && !treatmentData.isInTreatment)) &&
    !donorInActiveCycle
  ) {
    // FR users see a coming-soon placeholder — the helper paths (donor
    // screening, surrogacy agreements) are US-specific in this iteration.
    if (region !== "US") {
      return (
        <GlassScreenLayout>
          <ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
          >
            <ScreenHeader title={greeting} />
            <View
              style={{
                ...(GLASS_CARD_STYLE as object),
                padding: 24,
                alignItems: "center",
                gap: 12,
                marginTop: 12,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 18,
                  fontWeight: "600",
                  color: EMBER.colors.text,
                  textAlign: "center",
                }}
              >
                {t("home.helper_not_available_title")}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  color: EMBER.colors.textLight,
                  textAlign: "center",
                  lineHeight: 21,
                }}
              >
                {t("home.helper_not_available_body")}
              </Text>
            </View>
          </ScrollView>
        </GlassScreenLayout>
      );
    }

    if (effectiveHelperRole === "carrier") {
      const carrierScenario: CarrierJourneyScenario =
        homeExperience.scenario === "carrier_screening" ||
        homeExperience.scenario === "carrier_matched_legal"
          ? homeExperience.scenario
          : "carrier_application";

      return (
        <GlassScreenLayout>
          <ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
          >
            <ScreenHeader title={greeting} />

            <CarrierGuidedHome
              scenario={carrierScenario}
              relationship={homeExperience.relationship}
              onAction={handleAction}
            />

            <CompensationPlaceholderCard
              role="carrier"
              onOpenMedicalHistory={() =>
                handleAction({
                  label: "Medical history",
                  screen: "MedicalHistory",
                })
              }
            />

            <HelperHomeActionCard
              imageSource={learnIcon}
              title={t("home.learn_card.title")}
              subtitle={t("home.learn_card.subtitle")}
              onPress={() =>
                handleAction({ label: "Learn", screen: "LearnScreen" })
              }
            />

            <View style={{ marginTop: 24 }}>
              <RecordsSection
                onOpenCategory={(category: RecordsCategory) =>
                  handleAction({
                    label: "Documents",
                    screen: "DocumentsScreen",
                    params: {
                      initialCategory:
                        RECORDS_CATEGORY_TO_DOC_CATEGORY[category],
                    },
                  })
                }
                onOpenImport={() =>
                  openDocumentUpload({
                    defaultCategory: null,
                  })
                }
                onViewAll={() =>
                  handleAction({
                    label: "Documents",
                    screen: "DocumentsScreen",
                  })
                }
              />
            </View>
          </ScrollView>
        </GlassScreenLayout>
      );
    }

    const upcoming = getHelperUpcomingSteps(t)[effectiveHelperRole];
    const totalSteps = upcoming.length + 1;
    return (
      <GlassScreenLayout>
        <ScrollView
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
            paddingBottom: 100,
            maxWidth: 520,
            width: "100%",
            alignSelf: "center",
          }}
          showsVerticalScrollIndicator={false}
        >
          <ScreenHeader title={greeting} />

          <HelperJourneyHero
            role={effectiveHelperRole}
            onCompleteMedicalHistory={() =>
              handleAction({
                label: "Medical history",
                screen: "MedicalHistory",
              })
            }
          />

          <MedicalHistoryStepCard
            stepNumber={1}
            totalSteps={totalSteps}
            onAction={handleAction}
          />

          {upcoming.map((step, i) => (
            <HelperJourneyStep
              key={step.title}
              stepNumber={i + 2}
              totalSteps={totalSteps}
              title={step.title}
              subtitle={step.subtitle}
              imageSource={step.action?.imageSource}
              onPress={
                step.action
                  ? () =>
                      handleAction({
                        label: step.title,
                        screen: step.action!.screen,
                      })
                  : undefined
              }
            />
          ))}

          {effectiveHelperRole === "donor" && (
            <HelperHomeActionCard
              Icon={BookOpen}
              title="Full donor guide"
              subtitle="Screening, medication, retrieval, recovery, and your rights."
              onPress={() =>
                handleAction({
                  label: "Full donor guide",
                  screen: "ArticleDetail",
                  params: { articleId: EGG_DONATION_GUIDE_ARTICLE_ID },
                })
              }
            />
          )}

          <HelperHomeActionCard
            imageSource={learnIcon}
            title={t("home.learn_card.title")}
            subtitle={t("home.learn_card.subtitle")}
            onPress={() =>
              handleAction({ label: "Learn", screen: "LearnScreen" })
            }
          />

          <View style={{ marginTop: 24 }}>
            <RecordsSection
              onOpenCategory={(category: RecordsCategory) =>
                handleAction({
                  label: "Documents",
                  screen: "DocumentsScreen",
                  params: {
                    initialCategory: RECORDS_CATEGORY_TO_DOC_CATEGORY[category],
                  },
                })
              }
              onOpenImport={() =>
                openDocumentUpload({
                  defaultCategory: null,
                })
              }
              onViewAll={() =>
                handleAction({ label: "Documents", screen: "DocumentsScreen" })
              }
            />
          </View>
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  if (DEV_HOMEPAGE_ARCHIVE) {
    return (
      <GlassScreenLayout
        overlay={
          <>
            <RoadmapModal
              visible={journeyMapVisible}
              currentPhaseId={resolveRoadmapPhaseId(
                patient?.fertilityIntent?.stage,
                patient?.fertilityIntent?.goal ??
                  patient?.treatments?.[0]?.intent ??
                  null,
                cycles,
              )}
              journeyPath={resolveJourneyPath(
                cycles,
                patient?.fertilityIntent?.goal ??
                  patient?.treatments?.[0]?.intent ??
                  null,
              )}
              signals={journeySignals}
              onClose={() => setJourneyMapVisible(false)}
            />
            <InsuranceInfoModal
              visible={insuranceModalVisible}
              onClose={() => setInsuranceModalVisible(false)}
              primaryPolicy={primaryPolicy}
              hasInsurance={hasInsurance}
              verificationStatus={verificationStatus}
              fertilityCoverage={fertilityCoverage}
              isLoading={coverageLoading}
            />
            <IntentPickerModal
              visible={intentPickerVisible}
              currentIntent={patient?.fertilityIntent?.goal ?? null}
              onSelect={handleSelectIntent}
              onClose={() => setIntentPickerVisible(false)}
              isSaving={
                correctFertilityGoal.isPending || updatePatient.isPending
              }
              saveError={
                correctFertilityGoal.isError || updatePatient.isError
                  ? t("you.intent_picker.journey_save_failed")
                  : null
              }
            />
            <SupplementDetailSheet
              item={selectedSupplement}
              onClose={() => setSelectedSupplement(null)}
            />
            {partnerSourceOverlays}
          </>
        }
      >
        <ScrollView
          ref={scrollViewRef}
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
            paddingBottom: 100,
            maxWidth: 520,
            width: "100%",
            alignSelf: "center",
          }}
          showsVerticalScrollIndicator={false}
        >
          <ScreenHeader
            title="Homepage archive"
            subtitle="Dev-only parking lot for components removed from active homepages."
          />
          <HomepageComponentArchive
            userAge={userAge}
            journey={journey}
            progress={progress}
            costEstimate={costEstimate}
            onAction={handleAction}
            onOpenSupplement={handleOpenSupplement}
          />
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  return (
    <GlassScreenLayout
      overlay={
        <>
          <RoadmapModal
            visible={journeyMapVisible}
            currentPhaseId={resolveRoadmapPhaseId(
              patient?.fertilityIntent?.stage,
              patient?.fertilityIntent?.goal ??
                patient?.treatments?.[0]?.intent ??
                null,
              cycles,
            )}
            journeyPath={resolveJourneyPath(
              cycles,
              patient?.fertilityIntent?.goal ??
                patient?.treatments?.[0]?.intent ??
                null,
            )}
            signals={journeySignals}
            onClose={() => setJourneyMapVisible(false)}
          />
          <OutcomeFunnelModal
            visible={outcomeFunnelVisible}
            cycleType={treatmentData.cycleType}
            frozenEggs={
              totalEggs ||
              (completeData.cryoStorage?.itemType === "eggs"
                ? completeData.cryoStorage.itemCount
                : 0)
            }
            frozenEmbryos={
              totalEmbryos ||
              (completeData.cryoStorage?.itemType === "embryos"
                ? completeData.cryoStorage.itemCount
                : 0)
            }
            patientAge={userAge}
            onClose={() => setOutcomeFunnelVisible(false)}
          />
          <InsuranceInfoModal
            visible={insuranceModalVisible}
            onClose={() => setInsuranceModalVisible(false)}
            primaryPolicy={primaryPolicy}
            hasInsurance={hasInsurance}
            verificationStatus={verificationStatus}
            fertilityCoverage={fertilityCoverage}
            isLoading={coverageLoading}
          />
          <IntentPickerModal
            visible={intentPickerVisible}
            currentIntent={patient?.fertilityIntent?.goal ?? null}
            onSelect={handleSelectIntent}
            onClose={() => setIntentPickerVisible(false)}
            isSaving={correctFertilityGoal.isPending || updatePatient.isPending}
            saveError={
              correctFertilityGoal.isError || updatePatient.isError
                ? t("you.intent_picker.journey_save_failed")
                : null
            }
          />
          <SupplementDetailSheet
            item={selectedSupplement}
            onClose={() => setSelectedSupplement(null)}
          />
          <MedicationFactsheetSheet
            item={reviewMedicationSheet?.item ?? null}
            fallbackName={reviewMedicationSheet?.fallbackName}
            userContext={{
              ...reviewMedicationSheet?.userContext,
              source:
                allPatientMeds?.find(
                  (p) => p.id === reviewMedicationSheet?.medicationId,
                )?.source ?? null,
            }}
            medicationId={reviewMedicationSheet?.medicationId}
            onClose={() => setReviewMedicationSheet(null)}
            onLogDosePress={
              reviewMedicationSheet?.onLogDose
                ? () => {
                    const openDoseLogger = reviewMedicationSheet.onLogDose;
                    setReviewMedicationSheet(null);
                    openDoseLogger?.();
                  }
                : undefined
            }
            onEditPress={
              reviewMedicationSheet?.medicationId
                ? () => {
                    const id = reviewMedicationSheet.medicationId;
                    const name = reviewMedicationSheet.fallbackName;
                    setReviewMedicationSheet(null);
                    if (id) handleEditMedicationById(id, name);
                  }
                : undefined
            }
          />
          <AddMedicationModal
            visible={!!editMed}
            onClose={() => setEditMed(null)}
            cycleId=""
            editMedication={editMed}
          />
          <ProcedureDetailSheet
            item={reviewProcedureSheet?.item ?? null}
            appointment={
              reviewProcedureSheet
                ? {
                    type: "appointment",
                    id: reviewProcedureSheet.appointment.id,
                    appointmentId:
                      reviewProcedureSheet.appointment.appointmentId,
                    title: reviewProcedureSheet.appointment.title,
                    subtitle: reviewProcedureSheet.appointment.subtitle,
                    time: reviewProcedureSheet.appointment.time,
                    date: reviewProcedureSheet.appointment.date,
                    icon: CalendarClock,
                    factsheetId: reviewProcedureSheet.item.id,
                  }
                : undefined
            }
            onClose={() => setReviewProcedureSheet(null)}
          />
          <TestDetailSheet
            item={reviewTestSheet?.item ?? null}
            onClose={() => setReviewTestSheet(null)}
            returnTab="Home"
          />
          {partnerSourceOverlays}
        </>
      }
    >
      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <ScreenHeader title={greeting} />

        {/* Role-adapted top banner — coming-soon support for donor/carrier/intended-parent;
            self-hides when helperRole is null or the feature flag is off */}
        <RoleAdaptedHomeBanner />

        {showPendingSemenAnalysisCard && (
          <View style={{ marginTop: 16 }}>
            <TestResultInsightCardWithDetail
              interpretation={buildPendingSemenAnalysisInsight(t)}
              state={{
                kind: "waiting",
                label: "Waiting for results",
                expectedLabel: "2-5 days",
              }}
            />
          </View>
        )}

        {/* The ending arrives before the rest of Home: a cycle that has just
            closed is the most recent thing that happened, so it sits above the
            ongoing surfaces rather than below them. Acknowledging retires it.

            Rendered here, above every Home surface, because "above" was only
            true for some of them. It used to sit after the pre-cycle blocks, so
            on the Home a patient sees before her first cycle — which is where a
            finished preservation cycle still lands her, since `useCompleteCycle`
            never advances `homepageStage` — "you went all the way" appeared
            *below* a card asking her to choose a journey, and below the handoff
            card that is supposed to close the page.

            Suppressing it there was the first attempt and it was wrong: this
            carousel is one of only two writers of `outcome.acknowledgedAt` (the
            other is the post-result hero, which does not mount on a pre-cycle
            Home), and the arrival window is 30 days — so hiding it did not defer
            the ending, it stranded it, unannounced and unacknowledgeable for
            good. Moving it costs nothing and keeps that path open.

            Not on a post-result Home. That surface is already built around this
            ending — its hero states the result and carries the acknowledgement —
            so the carousel above it says the same thing a second time, in
            different words, and pushes the hero down the page. Worse, the two are
            resolved by different code: the carousel could announce a loss while
            the hero underneath described something else. One ending, one voice. */}
        {closingContent &&
          !closingRetired &&
          !heroCarriesTheEnding &&
          isCycleClosingArrival(homeCycleEnding) &&
          closingEndedRecently && (
            <CycleClosingCarousel
              content={closingContent}
              storedCount={completeData.cryoStorage?.itemCount ?? null}
              onDone={handleAcknowledgeClosing}
              onOpenCycle={openEndedCycle}
              isSaving={acknowledgeClosing.isPending}
              // A partner reads the ending but does not acknowledge it on the
              // patient's behalf — `acknowledgedAt` is on the shared outcome.
              canAcknowledge={!isPartner}
            />
          )}
        {showPreCycleEggFreezingConcept && !reviewConceptVariant && (
          <PreCycleEggFreezingHome
            activeStep={activeStep}
            steps={journey.steps.map((step, i) => ({
              id: step.id,
              title: step.title,
              icon: step.icon,
              status: progress[i]?.status ?? "not-started",
            }))}
            completedSteps={
              progress.filter((step) => step.status === "complete").length
            }
            totalSteps={journey.steps.length}
            userAge={userAge}
            onAction={handleAction}
            onOpenPlan={handleOpenFullPlan}
            onEditPlan={handleOpenIntentPicker}
          />
        )}
        {showPreCycleEmbryoFreezingConcept && !reviewConceptVariant && (
          <PreCycleEmbryoFreezingHome
            activeStep={activeStep}
            steps={journey.steps.map((step, i) => ({
              id: step.id,
              title: step.title,
              icon: step.icon,
              status: progress[i]?.status ?? "not-started",
            }))}
            completedSteps={
              progress.filter((step) => step.status === "complete").length
            }
            totalSteps={journey.steps.length}
            userAge={userAge}
            onAction={handleAction}
            onOpenPlan={handleOpenFullPlan}
            onEditPlan={handleOpenIntentPicker}
          />
        )}
        {showPreCycleSpermFreezingConcept && !reviewConceptVariant && (
          <PreCycleSpermFreezingHome
            activeStep={activeStep}
            steps={journey.steps.map((step, i) => ({
              id: step.id,
              title: step.title,
              icon: step.icon,
              status: progress[i]?.status ?? "not-started",
            }))}
            completedSteps={
              progress.filter((step) => step.status === "complete").length
            }
            totalSteps={journey.steps.length}
            userAge={userAge}
            onAction={handleAction}
            onOpenPlan={handleOpenFullPlan}
            onEditPlan={handleOpenIntentPicker}
          />
        )}
        {guidedPreCycleConceptVariant && (
          <GuidedPreCycleConceptHome
            variant={guidedPreCycleConceptVariant}
            userAge={userAge}
            viewerSexAtBirth={homeExperience.context.viewer.sexAtBirth}
            onAction={handleAction}
            onOpenPlan={handleOpenFullPlan}
            onEditPlan={handleOpenIntentPicker}
            scheduledCycleStartDate={scheduledPreCycleStartDate}
          />
        )}
        {reviewDashboardConceptVariant && (
          <HomepageReviewConceptHome
            variant={reviewDashboardConceptVariant}
            cycleStatusReviewKind={cycleStatusReviewKind}
            testInsights={testInsights}
            testResultsQueryState={testResultsQueryState}
            projectionSourceCycleId={projectionSourceCycleId}
            // A closed cycle is absent from /active-cycle, so the hero needs the id
            // to reach its own outcome. Sourced from the same closed-cycle reading
            // that selected the variant, so the two can never disagree.
            outcomeCycleId={
              closedEndingDrivesExperience && isPostProcedureOutcomeHome
                ? homeCycleEnding.cycleId
                : null
            }
            // Same flag the closing carousel already uses, so the two surfaces
            // cannot end up addressing different people about one ending.
            // Only a destination, never a record to read — see the prop's own note.
            endedCycleId={
              closedEndingDrivesExperience ? homeCycleEnding.cycleId : null
            }
            // Acknowledging is only worth a button where it hands Home back to
            // something. Resolved here because it is a question about the whole
            // cycle collection, which the hero does not have.
            endingInterruptsAnotherSubject={endingInterruptsAnotherSubject}
            isAcknowledgingEnding={acknowledgeClosing.isPending}
            acknowledgeEndingFailed={!!acknowledgeClosing.error}
            viewerIsSupporting={isPartner}
            onScrollToY={scrollHomeToY}
            onAction={handleAction}
            onOpenPregnancyTrackingSetup={() =>
              setPregnancyTrackingDialogVisible(true)
            }
            renderMonitoringCard={(widget) => (
              <OvarianStimulationMonitoringSection
                cycleId={transitionCycle.cycle?.id}
                cycleStartDate={transitionCycle.cycle?.startDate}
                phases={transitionCycle.phases}
                derivedStepProgress={[]}
                title={widget.title}
                titleMarginTop={0}
                defaultView="table"
                onUploadReport={(
                  dateISO,
                  onMonitoringExtracted,
                  onDocumentSaved,
                ) => {
                  void pickAndOpenDocumentUpload({
                    defaultCategory: "medical_records",
                    defaultTags: [
                      "monitoring-report",
                      `monitoring-date:${dateISO}`,
                    ],
                    onMonitoringExtracted,
                    onSave: onDocumentSaved,
                  });
                }}
              />
            )}
            onOpenSchedule={() =>
              handleAction({
                label: t(
                  "homepage.review_concept.hero_dynamic.cta.record_my_beta",
                ),
                screen: "CycleDetail",
                // A focus is required at all: without one, an id-less
                // cycle-detail action is read as a creation CTA, so this button
                // offered to start a treatment instead of opening the cycle.
                //
                // `"outcome"` because that is the one the screen answers to.
                // `"beta"` named the right intent and nothing read it —
                // `CycleDetailScreen` resolves `focus` for `"outcome"` and
                // `"monitoring"` only — so the press landed at the top of the
                // cycle and left her to find the step herself. `"outcome"`
                // scrolls the treatment hub into view and passes the step on as
                // `requestedStepId`, which is where the beta result is entered.
                params: { focus: "outcome" },
              })
            }
            onOpenJourneyMap={() => setJourneyMapVisible(true)}
            onOpenOutcomeFunnel={() => setOutcomeFunnelVisible(true)}
            onOpenMedicationFactSheet={handleOpenReviewMedicationFactSheet}
            onOpenProcedureFactSheet={handleOpenReviewProcedureFactSheet}
            onRequestDocumentAttachment={(row) =>
              pickAndOpenDocumentUpload({
                defaultCategory: "medical_records",
                defaultTags: [`today-action:${row.appointmentId ?? row.id}`],
              })
            }
            onOpenTestFactSheet={handleOpenReviewTestFactSheet}
            todayRows={todayConceptRows}
            peekAheadDays={peekAheadDays}
            afterTodaySlot={
              isTwwHome ? (
                <>
                  {/* Two-week wait: chat entry to log how they feel (better
                      than a symptom checklist, since Louise answers and
                      captures symptoms and worries), plus a curated reading
                      list for the stage. Sits above Testing / Good to know. */}
                  <AiAssistantCard
                    expanded={waitChatExpanded}
                    onToggle={toggleWaitChat}
                    progress={waitChatProgress}
                    returnTab="Home"
                    title={t("homepage.review_concept.wait_extras.chat_title")}
                    subtitle={t(
                      "homepage.review_concept.wait_extras.chat_subtitle",
                    )}
                    emptyPrompt={t(
                      "homepage.review_concept.wait_extras.chat_empty_prompt",
                    )}
                    inputPlaceholder={t(
                      "homepage.review_concept.wait_extras.chat_input_placeholder",
                    )}
                  />
                  {waitReadings.length > 0 && (
                    <ArticleSectionCard
                      label={t(
                        "homepage.review_concept.wait_extras.readings_label",
                      )}
                      articles={waitReadings}
                      isRead={isRead}
                      onOpenArticle={(article) =>
                        navigation.dispatch(
                          CommonActions.navigate({
                            name: "Learn",
                            params: {
                              screen: "ArticleDetail",
                              params: {
                                articleId: article.id,
                                returnTab: "Home",
                              },
                            },
                          }),
                        )
                      }
                    />
                  )}
                </>
              ) : undefined
            }
            endOfPageSlot={
              !isPostProcedureOutcomeHome && stimReadings.length === 1 ? (
                <ArticleBannerCard
                  label={t(
                    "homepage.review_concept.stim_extras.readings_label",
                  )}
                  article={stimReadings[0]!}
                  onPress={() =>
                    navigation.dispatch(
                      CommonActions.navigate({
                        name: "Learn",
                        params: {
                          screen: "ArticleDetail",
                          params: {
                            articleId: stimReadings[0]!.id,
                            returnTab: "Home",
                          },
                        },
                      }),
                    )
                  }
                />
              ) : !isPostProcedureOutcomeHome && stimReadings.length > 1 ? (
                <ArticleSectionCard
                  label={t(
                    "homepage.review_concept.stim_extras.readings_label",
                  )}
                  articles={stimReadings}
                  isRead={isRead}
                  onOpenArticle={(article) =>
                    navigation.dispatch(
                      CommonActions.navigate({
                        name: "Learn",
                        params: {
                          screen: "ArticleDetail",
                          params: {
                            articleId: article.id,
                            returnTab: "Home",
                          },
                        },
                      }),
                    )
                  }
                />
              ) : undefined
            }
          />
        )}

        {isPostProcedureOutcomeHome &&
        reviewDashboardConceptVariant?.endsWith("-positive") ? (
          <PregnancyTrackingSetupDialog
            visible={pregnancyTrackingDialogVisible}
            followUp={closingPregnancyFollowUp.data ?? null}
            onClose={() => setPregnancyTrackingDialogVisible(false)}
            onAction={handleAction}
          />
        ) : null}

        <PostProcedureTransitionDialog
          candidate={postProcedureCandidate}
          visible={postProcedureDialogOpen}
          isSaving={postProcedureSaving}
          error={postProcedureError}
          onClose={handleDismissPostProcedureDialog}
          onResolve={handlePostProcedureTransition}
        />

        <EndCycleModal
          visible={endCycleVisible}
          onClose={() => {
            setEndCycleVisible(false);
            // Dismissing without saving abandons the cancellation. Leaving it
            // pending meant a later close — from the hero, days afterwards —
            // inherited it and wrote a cancellation note for whatever ending the
            // patient chose then.
            setPendingCycleCancellation(null);
          }}
          cycleId={transitionCycle.cycle?.id}
          outcomeId={transitionCycle.cycle?.outcome?.id ?? null}
          preservationCycleType={transitionCycle.cycle?.cycleType}
          // The modal has to send back the outcome fields it does not own, or the
          // PATCH replaces them with nulls.
          existingOutcome={transitionCycle.cycle?.outcome ?? null}
          onBeforeSave={async () => {
            // `usePastCycleDetail` reads cached phase data for a closed cycle, so
            // a cycle closed without a snapshot loses it. Best effort: it must
            // not stop the patient ending their cycle.
            const id = transitionCycle.cycle?.id;
            if (!id) return;
            await createTreatmentSnapshot.mutateAsync(id).catch(() => {});
          }}
          transferDate={
            transitionCycle.phases.find(
              (phase) =>
                phase.type === "transfer" || phase.type === "insemination",
            )?.startDate ?? null
          }
          hasPregnancyOpportunity={hasConfirmedPregnancyProcedure(
            transitionCycle.phases,
          )}
          onSaved={(savedOutcomeType) => {
            const pending = pendingCycleCancellation;
            setEndCycleVisible(false);
            setPendingCycleCancellation(null);
            // Only record the cancellation when the patient actually cancelled.
            // This modal was opened expecting one, but all five endings are
            // offered once it is open — and it is now also reachable from the
            // hero, which carries no expectation at all. Writing the note
            // regardless put a "cancelled" entry in the record of a cycle the
            // patient had just closed as a pregnancy.
            if (pending && savedOutcomeType === "cancelled") {
              void writePostProcedureTransitionRecord(
                pending.candidate,
                "cancelled",
                pending.candidate.procedureDate,
                pending.notes,
              );
            }
          }}
          onOpenArticle={(articleId: string) => {
            const rootNav =
              navigation.getParent<NavigationProp<MainTabParamList>>();
            rootNav?.navigate("Learn", {
              screen: "ArticleDetail",
              params: { articleId, returnTab: "Home" },
            });
          }}
        />

        {/* Main content area — spine changes by state:
              - partner viewer: consent-scoped Partner Home boundary
              - cycle complete: CycleCompleteView
              - in-treatment: TreatmentDashboard (the cycle is the spine)
              - redesigned pre-cycle/wait/result concepts render above */}
        {isPartner ? (
          <View style={{ ...GLASS_CARD_STYLE, padding: 20, marginTop: 16 }}>
            {fetParticipantView ? (
              <FETParticipantHomeView view={fetParticipantView} />
            ) : (
              <PartnerHomeUnavailableView />
            )}
          </View>
        ) : isJourneyComplete ? (
          <View style={{ ...GLASS_CARD_STYLE, padding: 20, marginTop: 16 }}>
            <CycleCompleteView
              journeyTitle={journey.title}
              result={completeData.result}
              cryoStorage={completeData.cryoStorage}
              whatsNextOptions={completeData.whatsNextOptions}
              userAge={userAge}
              onAction={handleAction}
            />
          </View>
        ) : showSuggestedPreCycleHome ? null : treatmentData.isInTreatment ? (
          <View style={{ marginTop: 16 }}>
            {/* Result cards (data-first). Tap opens the canonical result dialog
                with thresholds, sources, and disclaimer per issue #1593.
                The wrapper handles the sheet mount internally. */}
            {testInsights.map((interp, i) => (
              <View
                key={`hero-result-${resultInterpretationKey(interp, i)}`}
                style={{ marginTop: 16 }}
              >
                <TestResultInsightCardWithDetail interpretation={interp} />
              </View>
            ))}
            {DEV_PREVIEW_ALL_CARDS && testInsights.length === 0
              ? getMockTestInsightsPreview(t).map((interp, i) => (
                  <View
                    key={`hero-result-preview-${resultInterpretationKey(interp, i)}`}
                    style={{ marginTop: 16 }}
                  >
                    <TestResultInsightCardWithDetail interpretation={interp} />
                  </View>
                ))
              : null}

            {/* Today — concept agenda rows shared with the redesigned Care
                calendar and cycle detail. */}
            <View>
              {todayConceptRows.length > 0 ? (
                <View style={{ marginTop: 16 }}>
                  <SectionTitleRow
                    title={t("care.today.title")}
                    icon={CalendarClock}
                    action={{
                      label: "Calendar",
                      onPress: () =>
                        handleAction({
                          label: "Calendar",
                          screen: "CareOverview",
                        }),
                    }}
                    style={{ marginBottom: 10, paddingHorizontal: 2 }}
                  />
                  <TodayList
                    rows={todayConceptRows}
                    onOpenMedicationFactSheet={
                      handleOpenReviewMedicationFactSheet
                    }
                    onOpenProcedureFactSheet={
                      handleOpenReviewProcedureFactSheet
                    }
                    onRequestDocumentAttachment={(row) =>
                      pickAndOpenDocumentUpload({
                        defaultCategory: "medical_records",
                        defaultTags: [
                          `today-action:${row.appointmentId ?? row.id}`,
                        ],
                      })
                    }
                  />
                </View>
              ) : null}
            </View>

            {/* What to know — only show clinical copy that is actually
                available for this patient. Empty skeleton cards imply a
                personalized protocol that the product does not have yet. */}
            {(treatmentData.sideEffects || treatmentData.whenToCall) && (
              <View style={{ marginTop: 24 }}>
                <View style={{ marginBottom: 12, paddingHorizontal: 2 }}>
                  <SectionTitle>{t("home.what_to_know")}</SectionTitle>
                </View>

                <View style={{ gap: 12 }}>
                  {treatmentData.sideEffects ? (
                    <View style={{ ...GLASS_CARD_STYLE, padding: 16 }}>
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 8,
                        }}
                      >
                        <Activity
                          size={14}
                          color={EMBER.colors.textLight}
                          strokeWidth={1.75}
                        />
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 15,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                          }}
                        >
                          {t("home.side_effects_title")}
                        </Text>
                      </View>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 13,
                          lineHeight: 19,
                          color: EMBER.colors.textMid,
                          fontWeight: "400",
                        }}
                      >
                        {treatmentData.sideEffects}
                      </Text>
                      <Pressable
                        onPress={() => {
                          capturePress(
                            pressLabel(t("home.log_my_symptoms")),
                            "home.log_my_symptoms",
                          );
                          handleLogSymptoms();
                        }}
                        style={({ pressed }) => ({
                          marginTop: 12,
                          alignSelf: "flex-start",
                          paddingHorizontal: 14,
                          paddingVertical: 8,
                          borderRadius: 999,
                          borderWidth: 1,
                          borderColor: "rgba(0,0,0,0.12)",
                          backgroundColor: pressed
                            ? "rgba(0,0,0,0.04)"
                            : "rgba(255,255,255,0.5)",
                        })}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                          }}
                        >
                          {t("home.log_my_symptoms")}
                        </Text>
                      </Pressable>
                    </View>
                  ) : null}

                  {treatmentData.whenToCall ? (
                    <View style={{ ...GLASS_CARD_STYLE, padding: 16 }}>
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 8,
                        }}
                      >
                        <PhoneCall
                          size={14}
                          color={EMBER.colors.textLight}
                          strokeWidth={1.75}
                        />
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 15,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                          }}
                        >
                          {t("home.when_to_call_clinic")}
                        </Text>
                      </View>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 13,
                          lineHeight: 19,
                          color: EMBER.colors.textMid,
                          fontWeight: "400",
                        }}
                      >
                        {treatmentData.whenToCall}
                      </Text>
                      <Pressable
                        onPress={() => {
                          capturePress(
                            pressLabel(
                              t(
                                clinicPhone
                                  ? "home.call_my_clinic"
                                  : "home.add_my_clinic",
                              ),
                            ),
                            "clinic_quick_action",
                          );
                          (clinicPhone ? handleCallClinic : handleAddClinic)();
                        }}
                        style={({ pressed }) => ({
                          marginTop: 12,
                          alignSelf: "flex-start",
                          paddingHorizontal: 14,
                          paddingVertical: 8,
                          borderRadius: 999,
                          borderWidth: 1,
                          borderColor: "rgba(0,0,0,0.12)",
                          backgroundColor: pressed
                            ? "rgba(0,0,0,0.04)"
                            : "rgba(255,255,255,0.5)",
                        })}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                          }}
                        >
                          {clinicPhone
                            ? t("home.call_my_clinic")
                            : t("home.add_my_clinic")}
                        </Text>
                      </Pressable>
                      {clinicPhone ? null : (
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 11,
                            fontWeight: "500",
                            color: EMBER.colors.textFaint,
                            marginTop: 6,
                          }}
                        >
                          {t("home.no_clinic_on_file")}
                        </Text>
                      )}
                    </View>
                  ) : null}
                </View>
              </View>
            )}
          </View>
        ) : null}

        {/* Optimization cards — both are framed for the pre-cycle long-
            runway window (CoQ10, weight, sleep over 3 months, etc), which
            does not apply once stim has started. Gated on !isInTreatment
            for now. Phase-appropriate optimization (hydration, electrolytes,
            comfort during stim; bed rest after retrieval; gentle activity
            in 2WW) is a separate need captured in A.1a and not yet built.
            Redesign of these cards is captured in A.1b. */}
        {!treatmentData.isInTreatment && !showSuggestedPreCycleHome && (
          <>
            {cards.map((card) => {
              if (card.type === "optimization-window" && card.data) {
                return (
                  <OptimizationCard
                    key={`opt-${card.stepId}`}
                    optimization={card.data as OptimizationWindow}
                    userAge={userAge}
                    onAction={handleAction}
                  />
                );
              }
              return null;
            })}
            {DEV_PREVIEW_ALL_CARDS &&
              !cards.some((c) => c.type === "optimization-window") && (
                <OptimizationCard
                  key="opt-preview"
                  optimization={MOCK_OPTIMIZATION_WINDOW}
                  userAge={userAge}
                  onAction={handleAction}
                />
              )}

            {/* V2 preview — design alternative for the optimization card.
                Renders only in dev preview mode so we can compare side-by-
                side without replacing the legacy card. Slated to retire
                the legacy card once the redesign in A.1b lands. */}
            {DEV_PREVIEW_ALL_CARDS && (
              <OptimizationCardV2
                optimization={MOCK_OPTIMIZATION_WINDOW}
                userAge={userAge}
              />
            )}
          </>
        )}

        {/* About you — persona-derived condition cards (PMOS (ex PCOS), endo,
            thyroid, etc) grouped under one section header. Test results
            were moved up under the hero (see A.1e), so this section is
            condition-only now. */}
        {!showSuggestedPreCycleHome &&
          (activeStepConditionContent.length > 0 || DEV_PREVIEW_ALL_CARDS) && (
            <View style={{ marginTop: 24 }}>
              <View style={{ marginBottom: 12, paddingHorizontal: 2 }}>
                <SectionTitle icon={UserCircle2}>
                  {t("home.about_you")}
                </SectionTitle>
              </View>

              <View style={{ gap: 12 }}>
                {activeStepConditionContent.map((content) => (
                  <ConditionInsightCard
                    key={`cond-${content.condition}-${content.stepId}`}
                    content={content}
                    onPressSupplement={handleOpenSupplement}
                  />
                ))}
                {DEV_PREVIEW_ALL_CARDS &&
                  activeStepConditionContent.length === 0 && (
                    <ConditionInsightCard
                      key="cond-preview"
                      content={MOCK_CONDITION_CONTENT}
                      onPressSupplement={handleOpenSupplement}
                    />
                  )}
              </View>
            </View>
          )}

        {/* Records — the canonical four-folder records surface. Outcome homes
            still need this handoff even when the active cycle has just closed;
            the result and procedure history remain part of the care story. */}
        {(treatmentData.isInTreatment ||
          isPostProcedureOutcomeHome ||
          isCycleStatusReviewHome ||
          isPostCycleHome) && (
          <RecordsSection
            topMargin={24}
            onOpenCategory={(category: RecordsCategory) =>
              handleAction({
                label: "Documents",
                screen: "DocumentsScreen",
                params: {
                  initialCategory: RECORDS_CATEGORY_TO_DOC_CATEGORY[category],
                },
              })
            }
            onOpenImport={() =>
              openDocumentUpload({
                defaultCategory: null,
              })
            }
            onViewAll={() =>
              handleAction({ label: "Documents", screen: "DocumentsScreen" })
            }
          />
        )}

        {/* Cost estimate card — pre-cycle only. In-cycle billing stays off the
            homepage until it has a real balance/payment data contract rather
            than showing a non-actionable placeholder. */}
        {!isPartner &&
          !isJourneyComplete &&
          !treatmentData.isInTreatment &&
          !showSuggestedPreCycleHome &&
          costEstimate && (
            <CostEstimateCard
              estimate={costEstimate}
              onUploadCoverage={() =>
                handleAction({
                  label: "Upload coverage",
                  screen: "CoverageFlow",
                })
              }
            />
          )}

        {postProcedureCandidate ? (
          <JourneyHandoffCard
            title={t(
              `homepage.review_concept.post_procedure.${
                postProcedureCandidate.procedure === "insemination"
                  ? "insemination"
                  : postProcedureCandidate.procedure === "ovulation_window"
                    ? "ovulation_window"
                    : "transfer"
              }.handoff_title`,
            )}
            body={t(
              `homepage.review_concept.post_procedure.${
                postProcedureCandidate.procedure === "insemination"
                  ? "insemination"
                  : postProcedureCandidate.procedure === "ovulation_window"
                    ? "ovulation_window"
                    : "transfer"
              }.handoff_body`,
            )}
            ctaLabel={t(
              `homepage.review_concept.post_procedure.${
                postProcedureCandidate.procedure === "insemination"
                  ? "insemination"
                  : postProcedureCandidate.procedure === "ovulation_window"
                    ? "ovulation_window"
                    : "transfer"
              }.handoff_cta`,
            )}
            checklist={[1, 2, 3].map((item) =>
              t(
                `homepage.review_concept.post_procedure.${
                  postProcedureCandidate.procedure === "insemination"
                    ? "insemination"
                    : postProcedureCandidate.procedure === "ovulation_window"
                      ? "ovulation_window"
                      : "transfer"
                }.handoff_check_${item}`,
              ),
            )}
            backgroundImage={handoffBackground}
            illustration={
              <OrganicExplanatoryIllustration kind="follicle-cohort" />
            }
            onPress={() => {
              setPostProcedureError(null);
              setPostProcedureDialogOpen(true);
            }}
          />
        ) : isPostProcedureOutcomeHome && postProcedureOutcomeContentKey ? (
          <JourneyHandoffCard
            title={t(outcomeHandoffCopy.titleKey)}
            body={t(outcomeHandoffCopy.bodyKey)}
            ctaLabel={t(outcomeHandoffCopy.ctaKey)}
            checklist={outcomeHandoffCopy.checklistKeys.map((key) => t(key))}
            backgroundImage={handoffBackground}
            illustration={
              <OrganicExplanatoryIllustration kind="follicle-cohort" />
            }
            secondaryAction={
              postProcedureOutcomeHandoffVariant === "negative"
                ? {
                    label: t(
                      postProcedureOutcomeHandoffCopyKey("secondary_cta"),
                    ),
                    onPress: () =>
                      handleAction({
                        label: t(
                          postProcedureOutcomeHandoffCopyKey("secondary_cta"),
                        ),
                        signal: "open-start-treatment-modal",
                        params: {
                          presetCycleType: postProcedureOutcomeContentKey,
                        },
                      }),
                  }
                : // A recorded beta is a result, not a pregnancy she has told us
                  // about — so the closing card asked her to set up week-by-week
                  // follow-up for something the app is not yet allowed to count.
                  // What the moment actually wants is the ending stated: still
                  // going, ended, a birth. That is what unlocks the dated steps,
                  // and it lives on the cycle's outcome step.
                  //
                  // Follow-up is not taken away, only stopped being pushed here:
                  // it stays offered in "When you're ready" (`readyRows`), which a
                  // standing pregnancy now shows without waiting to be
                  // acknowledged.
                  //
                  // `standingPregnancyOnHome` is what makes the offer honest: an
                  // ending already recorded — a birth or a loss — leaves nothing to
                  // specify, and a partner is not the one who records it. And it
                  // needs an open cycle to land on: on a closed one the outcome
                  // step is not reachable, so promising it would be a dead end.
                  postProcedureOutcomeHandoffVariant === "positive" &&
                    standingPregnancyOnHome &&
                    treatmentData.cycleId
                  ? {
                      label: t(
                        "homepage.review_concept.post_procedure_outcome.shared.specify_this_cycles_outcome",
                      ),
                      onPress: () =>
                        handleAction({
                          label: t(
                            "homepage.review_concept.post_procedure_outcome.shared.specify_this_cycles_outcome",
                          ),
                          // Through the signal, never a hand-built
                          // `screen: "CycleDetail"`. An id-less cycle-detail action
                          // resolves to `start-treatment` whenever
                          // `isInTreatment` is false — which is exactly the state a
                          // closed cycle driving this surface puts us in — so
                          // "specify this cycle's outcome" would have opened the
                          // start-a-new-treatment modal. The signal resolves the
                          // cycle here and forwards the focus.
                          signal: "open-recorded-cycle",
                          params: { focus: "outcome" },
                        }),
                    }
                  : undefined
            }
            onPress={() =>
              handleAction({
                label: t(outcomeHandoffCopy.ctaKey),
                signal: "open-ai-chat",
                params: {
                  initialPrompt: t(outcomeHandoffCopy.chatPromptKey),
                },
              })
            }
          />
        ) : isCycleStatusReviewHome ? (
          <JourneyHandoffCard
            title={t(`${cycleStatusReviewCopyPrefix}_handoff_title`)}
            body={t(`${cycleStatusReviewCopyPrefix}_handoff_body`)}
            ctaLabel={t(`${cycleStatusReviewCopyPrefix}_handoff_cta`)}
            checklist={[1, 2].map((item) =>
              t(`${cycleStatusReviewCopyPrefix}_handoff_check_${item}`),
            )}
            backgroundImage={handoffBackground}
            illustration={
              <OrganicExplanatoryIllustration kind="follicle-cohort" />
            }
            onPress={() =>
              handleAction({
                label: t(`${cycleStatusReviewCopyPrefix}_handoff_cta`),
                signal: "open-ai-chat",
                params: {
                  initialPrompt: t(
                    `${cycleStatusReviewCopyPrefix}_question_prompt`,
                  ),
                },
              })
            }
          />
        ) : null}

        {/**
         * The post-cycle Home's closing card, last on the page.
         *
         * Beside the chain above rather than inside it. As one more `else if` it
         * shadowed `postProcedureCandidate`, which is reachable on a post-cycle
         * variant — `resolveHomeSurface` returns `cycle-complete` from
         * `isJourneyComplete` before it tests `hasActiveCycle`, so a patient who
         * banked eggs and has since opened an IVF cycle with a performed
         * transfer resolves here with a candidate. That card is a dated,
         * actionable prompt and the only way back to recording the procedure
         * once its dialog has been dismissed; losing it to an open-ended chat
         * CTA would be the wrong trade. Both render, and this one is still last.
         */}
        {postCycleHandoffJourney ? (
          // The list this card came from sits in a container with `gap: 18`;
          // this ScrollView has none, so the separation is carried here.
          <View style={{ marginTop: 18 }}>
            <PostCycleHandoffCard
              journey={postCycleHandoffJourney}
              onAction={handleAction}
            />
          </View>
        ) : null}
      </ScrollView>
    </GlassScreenLayout>
  );
}
